<template>
  <div class="platform-bug-reporting-page container-fluid py-4">
    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <!-- ── 1. VIEW: MANAGED PLATFORMS GRID (currentView === 'dashboard') ── -->
    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <div v-if="currentView === 'dashboard'">
      <!-- Page Header -->
      <div class="ms-page-header mb-4">
        <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
          <div class="d-flex align-items-center gap-3">
            <div class="ms-page-header__icon">
              <i class="fa-solid fa-bug"></i>
            </div>
            <div>
              <p class="ms-page-header__breadcrumb">Platform Delivery <span class="ms-breadcrumb-sep">/</span> Platform Bug Reporting &amp; Feedback Hub</p>
              <h1 class="ms-page-header__title">Platform Bug Reporting &amp; Feedback Hub</h1>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <router-link
              to="/platform-settings/user-settings"
              class="btn btn-cyan-primary fw-bold px-3 py-2 text-sm d-flex align-items-center gap-2 text-decoration-none"
              title="Configure your personal Atlassian API token in User Settings"
            >
              <i class="fa-solid fa-key"></i>
              <span>My Atlassian Token</span>
            </router-link>
            <button 
              class="btn btn-cyan-primary fw-bold px-3 py-2 text-sm d-flex align-items-center gap-2"
              data-bs-toggle="modal" 
              data-bs-target="#platformFormModal" 
              @click="openAddPlatformModal"
              :disabled="isSubmitting"
            >
              <i class="fa-solid fa-plus"></i>
              <span>Register New Platform</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Search & Filters Toolbar -->
      <div class="card border-0 shadow-sm bg-white mb-4 filter-toolbar-card">
        <div class="card-body p-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1" style="max-width: 480px;">
            <div class="position-relative flex-grow-1">
              <i class="fa-solid fa-magnifying-glass text-muted-custom search-icon"></i>
              <input 
                type="text" 
                class="form-control form-control-sm ps-4 pe-4 bg-light border-0 focus-glow text-xs" 
                v-model="platformSearchQuery" 
                placeholder="Search managed platforms by name, key, or description..." 
              />
              <button 
                v-if="platformSearchQuery" 
                @click="platformSearchQuery = ''" 
                class="btn btn-link btn-xs text-muted-custom position-absolute end-0 top-50 translate-middle-y me-1 p-0 text-decoration-none"
              >
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
          </div>

          <div class="d-flex align-items-center gap-3 text-muted-custom text-xs fw-semibold flex-wrap">
            <span class="badge bg-light text-dark-blue border px-3 py-2">
              <i class="fa-solid fa-server me-1 text-info"></i> {{ filteredPlatforms.length }} Platforms Active
            </span>
            <span class="badge bg-danger-subtle text-danger border border-danger-subtle px-3 py-2">
              <i class="fa-solid fa-bug me-1"></i> {{ totalOpenBugsCount }} Open / In Progress
            </span>
            <span class="badge bg-success-subtle text-success border border-success-subtle px-3 py-2">
              <i class="fa-solid fa-circle-check me-1"></i> {{ totalResolvedBugsCount }} Resolved
            </span>
          </div>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div v-if="isLoadingPlatforms" class="text-center py-5">
        <div class="spinner-border text-info" role="status" style="width: 3rem; height: 3rem;">
          <span class="visually-hidden">Loading platforms...</span>
        </div>
        <p class="text-muted-custom mt-3 fw-bold">Loading bug reporting workspace...</p>
      </div>

      <!-- Platforms Grid -->
      <div v-else>
        <!-- Empty State -->
        <div v-if="filteredPlatforms.length === 0" class="card shadow-sm border-0 bg-white p-5 text-center text-muted">
          <i class="fa-solid fa-bug-slash fa-3x mb-3 text-light"></i>
          <h5 class="fw-bold text-dark-blue">No Platforms Found</h5>
          <p class="text-muted-custom text-xs mb-3">Register your first platform or web application to embed floating bug widgets and collect feedback.</p>
          <div>
            <button class="btn btn-cyan-primary btn-sm fw-bold px-3 py-2" data-bs-toggle="modal" data-bs-target="#platformFormModal" @click="openAddPlatformModal" :disabled="isSubmitting">
              <i class="fa-solid fa-plus me-1"></i> Register Platform
            </button>
          </div>
        </div>

        <!-- 3-Column Cards Grid -->
        <div v-else class="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 mb-5">
          <div v-for="plat in filteredPlatforms" :key="plat.id" class="col">
            <div class="card h-100 border-0 shadow-sm project-card bg-white" @click="selectPlatformView(plat)">
              <div class="card-body p-4 d-flex flex-column justify-content-between">
                <div>
                  <!-- Card Header: Logo + Title/Key + Status & Actions -->
                  <div class="d-flex justify-content-between align-items-start gap-2 mb-3">
                    <div class="d-flex align-items-center gap-3 min-w-0">
                      <div 
                        class="project-icon-wrapper flex-shrink-0" 
                        :style="{ 
                          backgroundColor: isUrl(plat.icon) ? '#ffffff' : hexToRgba(plat.primary_color || '#00a0c1', 0.12), 
                          color: plat.primary_color || '#00a0c1', 
                          border: isUrl(plat.icon) ? '1px solid #e2e8f0' : '1px solid ' + hexToRgba(plat.primary_color || '#00a0c1', 0.25)
                        }"
                      >
                        <img v-if="isUrl(plat.icon)" :src="plat.icon" class="project-card-logo" alt="" />
                        <i v-else :class="plat.icon || 'fa-solid fa-bug'"></i>
                      </div>
                      <div class="min-w-0">
                        <div class="d-flex align-items-center gap-2 flex-wrap">
                          <h4 class="fw-bold text-dark-blue mb-0 card-project-title text-truncate">{{ plat.name }}</h4>
                          <span class="badge bg-light text-secondary border text-xxs font-monospace px-2 py-0.5">{{ plat.key }}</span>
                        </div>
                        <div class="d-flex align-items-center gap-2 mt-1">
                          <span 
                            class="d-inline-flex align-items-center gap-1 text-xxs fw-bold"
                            :class="plat.widget_enabled !== false ? 'text-success' : 'text-secondary'"
                          >
                            <i class="fa-solid fa-circle" style="font-size: 6px;"></i>
                            {{ plat.widget_enabled !== false ? 'Widget Active' : 'Widget Disabled' }}
                          </span>
                          <span class="text-muted-custom text-xxs">&bull;</span>
                          <span class="text-muted-custom text-xxs fw-semibold">
                            {{ getPositionLabel(plat.widget_position) }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Actions (Settings & Delete) -->
                    <div class="d-flex align-items-center gap-1 flex-shrink-0">
                      <button 
                        class="btn btn-sm btn-link text-info border-0 p-1 card-settings-btn" 
                        @click.stop="openEditPlatformModal(plat)" 
                        data-bs-toggle="modal"
                        data-bs-target="#editPlatformModal"
                        title="Edit Platform Settings"
                      >
                        <i class="fa-solid fa-gear fs-6"></i>
                      </button>
                      <button 
                        class="btn btn-sm btn-link text-danger border-0 p-1 card-delete-btn" 
                        @click.stop="confirmDeletePlatform(plat)" 
                        data-bs-toggle="modal"
                        data-bs-target="#deletePlatformModal"
                        title="Delete Platform"
                      >
                        <i class="fa-solid fa-trash-can fs-6"></i>
                      </button>
                    </div>
                  </div>

                  <p class="text-muted-custom text-xs text-clamp mb-3" style="min-height: 36px; line-height: 1.5;">
                    {{ plat.description || 'In-app bug reporting and interactive feedback widget integration.' }}
                  </p>
                </div>

                <!-- Compact Metrics & Integration Footer -->
                <div>
                  <!-- 3-Column Issue & Repository Counters -->
                  <div class="row g-2 mb-3">
                    <div class="col-4">
                      <div class="rounded-3 bg-light border d-flex align-items-center justify-content-between px-2.5 py-2">
                        <span class="text-muted-custom text-xxs fw-bold text-uppercase">Open</span>
                        <span class="badge rounded-pill px-2 py-1 text-xxs fw-bold" :class="(plat.open_bugs_count ?? plat.open_bugs ?? 0) > 0 ? 'bg-danger-subtle text-danger border border-danger-subtle' : 'bg-white text-secondary border'">
                          {{ plat.open_bugs_count ?? plat.open_bugs ?? 0 }}
                        </span>
                      </div>
                    </div>
                    <div class="col-4">
                      <div class="rounded-3 bg-light border d-flex align-items-center justify-content-between px-2.5 py-2">
                        <span class="text-muted-custom text-xxs fw-bold text-uppercase">Resolved</span>
                        <span class="badge bg-success-subtle text-success border border-success-subtle rounded-pill px-2 py-1 text-xxs fw-bold">
                          {{ plat.resolved_bugs_count ?? plat.resolved_bugs ?? 0 }}
                        </span>
                      </div>
                    </div>
                    <div class="col-4">
                      <div
                        class="rounded-3 bg-light border d-flex align-items-center justify-content-between px-2.5 py-2"
                        :title="getPlatformReposTooltip(plat)"
                      >
                        <span class="text-muted-custom text-xxs fw-bold text-uppercase">Repos</span>
                        <span class="badge bg-cyan-subtle text-cyan border border-cyan-subtle rounded-pill px-2 py-1 text-xxs fw-bold">
                          <i class="fa-solid fa-code-branch me-1"></i>{{ getPlatformRepoCount(plat) }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Single-Row Integration Meta Strip -->
                  <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 px-2.5 py-2 mb-3 rounded-3 bg-light border text-xxs">
                    <a
                      :href="'https://' + (plat.jira_domain || 'vertex.atlassian.net') + '/jira/core/projects/' + (plat.jira_project_key || 'DEV') + '/board?filter=&groupBy=none'"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="badge bg-white text-primary border font-monospace px-2 py-1 text-decoration-none d-inline-flex align-items-center gap-1"
                      @click.stop
                      title="Open Jira Board"
                    >
                      <i class="fa-solid fa-diagram-project"></i>
                      <span>Jira Board: {{ plat.jira_project_key || 'DEV' }}</span>
                      <span v-if="plat.jira_auto_sync" class="text-success">(Auto)</span>
                    </a>
                    <button
                      type="button"
                      class="btn btn-xs btn-white bg-white border text-secondary font-monospace px-2 py-0.5 d-inline-flex align-items-center gap-1"
                      @click.stop="copyText(plat.widget_uuid)"
                      title="Copy Widget Security UUID"
                    >
                      <span>UUID: {{ (plat.widget_uuid || 'UUID').slice(0, 8) }}&hellip;</span>
                      <i class="fa-regular fa-copy text-info"></i>
                    </button>
                  </div>

                  <!-- Footer Action Link -->
                  <div class="d-flex align-items-center justify-content-between pt-2 border-top border-light text-cyan-primary fw-bold text-xs card-action-link">
                    <span>Open Bug Reporting Hub</span>
                    <i class="fa-solid fa-arrow-right-long card-arrow"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <!-- ── 2. VIEW: PLATFORM WORKSPACE DETAIL (currentView === 'platform') ── -->
    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <div v-else-if="currentView === 'platform'">
      <!-- Page Header -->
      <div v-if="!embeddedMode" class="ms-page-header mb-4">
        <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
          <div class="d-flex align-items-center gap-3 flex-wrap">
            <button 
              class="btn btn-header-secondary btn-sm fw-bold px-3 py-2 rounded-3 d-flex align-items-center gap-2 shadow-xs" 
              @click="currentView = 'dashboard'"
              title="Return to All Platforms Grid"
            >
              <i class="fa-solid fa-arrow-left text-info"></i>
              <span>All Platforms</span>
            </button>

            <div class="d-flex align-items-center gap-3">
              <div 
                class="ms-page-header__icon" 
                :style="{ backgroundColor: currentPlatform ? hexToRgba(currentPlatform.primary_color || '#00a0c1', 0.12) : '#f0f9ff' }"
              >
                <img v-if="currentPlatform && isUrl(currentPlatform.icon)" :src="currentPlatform.icon" class="platform-avatar-img" alt="" />
                <i v-else :class="currentPlatform ? currentPlatform.icon : 'fa-solid fa-bug'" :style="{ color: currentPlatform ? currentPlatform.primary_color : '#00a0c1' }"></i>
              </div>
              <div>
                <p class="ms-page-header__breadcrumb mb-1">Platform Delivery <span class="ms-breadcrumb-sep">/</span> Bug Reporting Workspace</p>
                <div class="d-flex align-items-center flex-wrap gap-2">
                  <h1 class="ms-page-header__title mb-0">{{ currentPlatform ? currentPlatform.name : 'Bug Reporting Workspace' }}</h1>
                  <span class="badge bg-light text-secondary border text-xs font-monospace px-2 py-1">{{ selectedPlatformKey }}</span>
                  <span class="badge bg-white text-dark-blue border text-xs font-monospace px-2 py-1">Platform ID: #{{ currentPlatform ? currentPlatform.id : 0 }}</span>
                  <a
                    :href="'https://' + (widgetForm.jira_domain || 'vertex.atlassian.net') + '/jira/core/projects/' + (widgetForm.jira_project_key || 'DEV') + '/board?filter=&groupBy=none'"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="badge bg-primary-subtle text-primary border border-primary-subtle text-xs font-monospace px-2 py-1 text-decoration-none"
                    title="Open this platform's configured Jira Board"
                  >
                    <i class="fa-solid fa-diagram-project me-1"></i>Jira Board: {{ widgetForm.jira_project_key || 'DEV' }}
                  </a>
                </div>
              </div>
            </div>

            <!-- Switcher Dropdown -->
            <div class="dropdown ms-2">
              <button 
                class="btn btn-white bg-white border shadow-xs rounded-3 px-3 py-2 d-flex align-items-center gap-2 text-dark-blue fw-bold text-xs platform-select-btn" 
                type="button" 
                id="platformDetailDropdown" 
                data-bs-toggle="dropdown" 
                aria-expanded="false" 
              >
                <span class="platform-color-dot" :style="{ backgroundColor: currentPlatform ? currentPlatform.primary_color : '#00a0c1' }"></span>
                <span>Switch Platform</span>
                <i class="fa-solid fa-chevron-down text-muted-custom text-xxs"></i>
              </button>
              <ul class="dropdown-menu dropdown-menu-end shadow border-0 p-2 rounded-3" aria-labelledby="platformDetailDropdown" style="min-width: 240px; z-index: 1050;">
                <li class="dropdown-header text-muted-custom text-xxs text-uppercase fw-bold px-2 py-1">Select Target Platform</li>
                <li v-for="plat in platforms" :key="plat.id">
                  <a 
                    class="dropdown-item rounded-2 py-2 px-2 text-xs d-flex align-items-center justify-content-between" 
                    :class="{ 'active bg-cyan-primary text-white': plat.key === selectedPlatformKey }"
                    href="#" 
                    @click.prevent="switchPlatform(plat.key)"
                  >
                    <div class="d-flex align-items-center gap-2">
                      <span class="platform-color-dot" :style="{ backgroundColor: plat.primary_color || '#00a0c1' }"></span>
                      <span class="fw-bold">{{ plat.name }}</span>
                    </div>
                    <span class="badge border font-monospace text-xxs" :class="plat.key === selectedPlatformKey ? 'bg-white text-dark' : 'bg-light text-secondary'">
                      {{ plat.key }}
                    </span>
                  </a>
                </li>
                <li class="border-top mt-2 pt-2">
                  <a 
                    class="dropdown-item rounded-2 text-info fw-bold py-2 px-2 text-xs d-flex align-items-center gap-2" 
                    href="#" 
                    data-bs-toggle="modal" 
                    data-bs-target="#platformFormModal"
                    @click="openAddPlatformModal"
                  >
                    <i class="fa-solid fa-plus-circle"></i>
                    <span>Register New Platform</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <!-- Header Right Actions -->
          <div class="d-flex align-items-center gap-2">
            <router-link
              to="/platform-settings/user-settings"
              class="btn btn-cyan-primary fw-bold d-flex align-items-center gap-2 px-3 py-2 text-sm text-decoration-none"
              title="Configure your personal Atlassian API token in User Settings"
            >
              <i class="fa-solid fa-key"></i>
              <span>My Atlassian Token</span>
            </router-link>
            <button 
              class="btn btn-header-secondary fw-bold d-flex align-items-center gap-2 px-3 py-2 text-sm"
              @click="activeTab = 'customizer'"
              title="Configure Widget Settings"
            >
              <i class="fa-solid fa-sliders text-info"></i>
              <span>Widget Settings</span>
            </button>
            <button 
              class="btn btn-cyan-primary fw-bold d-flex align-items-center gap-2 px-3 py-2 text-sm"
              @click="openSandboxModal"
            >
              <i class="fa-solid fa-bug"></i>
              <span>+ Report Bug / Test Widget</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Active Platform Context Banner Card (Matches PlatformReleases/Dashboard.vue) -->
      <div v-if="currentPlatform" class="card border-0 shadow-sm bg-white mb-4 platform-banner-card">
        <div class="card-body p-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div class="d-flex align-items-center gap-3">
            <div 
              class="platform-icon-avatar platform-banner-avatar" 
              :style="{ 
                backgroundColor: isUrl(currentPlatform.icon) ? '#ffffff' : hexToRgba(widgetForm.primary_color || currentPlatform.primary_color || '#00a0c1', 0.12), 
                color: widgetForm.primary_color || currentPlatform.primary_color || '#00a0c1',
                border: isUrl(currentPlatform.icon) ? '1px solid #e2e8f0' : 'none'
              }"
            >
              <img v-if="isUrl(currentPlatform.icon)" :src="currentPlatform.icon" class="platform-avatar-img platform-banner-logo-img" alt="" />
              <i v-else :class="currentPlatform.icon || 'fa-solid fa-bug'"></i>
            </div>
            <div>
              <div class="d-flex align-items-center flex-wrap gap-2 mb-1">
                <h4 class="fw-bold mb-0 text-dark-blue">{{ currentPlatform.name }}</h4>
                <span class="badge bg-light text-secondary border text-xs font-monospace px-2 py-1">{{ currentPlatform.key }}</span>
                <span 
                  class="badge text-xxs fw-bold px-2 py-1 rounded-pill"
                  :class="widgetForm.widget_enabled !== false ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-secondary-subtle text-secondary border border-secondary-subtle'"
                >
                  <i class="fa-solid fa-circle me-1" style="font-size: 7px; vertical-align: middle;"></i>
                  {{ widgetForm.widget_enabled !== false ? 'Widget Enabled' : 'Widget Disabled' }}
                </span>
                <span class="version-pill-highlight-sm font-monospace">
                  <i class="fa-solid fa-arrows-to-dot opacity-75"></i>
                  <span>{{ getPositionLabel(widgetForm.widget_position) }}</span>
                </span>
              </div>
              <p class="text-muted-custom text-xs mb-0" style="line-height: 1.5;">
                {{ currentPlatform.description || 'Centralized in-app bug reporting, annotated screenshots, and feedback triage hub.' }}
              </p>
            </div>
          </div>

          <div class="d-flex align-items-center gap-2 flex-wrap">
            <span class="badge bg-white text-dark-blue border text-xs px-3 py-2 fw-bold shadow-xs">
              <i class="fa-solid fa-bug text-danger me-1"></i> Open Issues: <span class="text-danger fw-bolder">{{ bugReports.filter(r => r.status === 'open' || r.status === 'in_progress').length }}</span>
            </span>
            <span class="badge bg-light text-dark-blue border text-xs px-3 py-2 fw-semibold">
              <i class="fa-solid fa-circle-check text-success me-1"></i> {{ bugReports.filter(r => r.status === 'resolved').length }} Resolved
            </span>
            <button 
              @click="copyCode(scriptSnippetCode)" 
              class="btn btn-outline-secondary btn-xs rounded-pill px-3 py-2 fw-bold"
              title="Copy <script> widget loader snippet"
            >
              <i class="fa-regular fa-copy me-1 text-info"></i> Copy Embed Snippet
            </button>
            <button 
              v-if="activeTab !== 'customizer'"
              @click="activeTab = 'customizer'" 
              class="btn btn-cyan-outline btn-xs rounded-pill px-3 py-2 fw-bold"
            >
              <i class="fa-solid fa-wand-magic-sparkles me-1"></i> Customize Widget
            </button>
            <button 
              v-else
              @click="activeTab = 'reports'" 
              class="btn btn-cyan-outline btn-xs rounded-pill px-3 py-2 fw-bold"
            >
              <i class="fa-solid fa-list-check me-1"></i> View Triage Board
            </button>
          </div>
        </div>
      </div>

      <!-- Navigation Tabs -->
      <ul class="nav nav-tabs mb-4" id="bugReportingTabs" role="tablist">
        <li class="nav-item" role="presentation">
          <button 
            class="nav-link px-4 py-3 fw-bold" 
            :class="{ active: activeTab === 'reports' }" 
            @click="activeTab = 'reports'" 
            type="button"
          >
            <i class="fa-solid fa-list-check me-2"></i>Bug Reports &amp; Feedback Triage
            <span class="badge bg-cyan-primary text-white ms-2 text-xxs px-2 py-1 rounded-pill">{{ bugReports.length }}</span>
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button 
            class="nav-link px-4 py-3 fw-bold" 
            :class="{ active: activeTab === 'customizer' }" 
            @click="activeTab = 'customizer'" 
            type="button"
          >
            <i class="fa-solid fa-wand-magic-sparkles me-2"></i>Widget Orientation, Customizer &amp; Live Sandbox
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button 
            class="nav-link px-4 py-3 fw-bold" 
            :class="{ active: activeTab === 'embed' }" 
            @click="activeTab = 'embed'" 
            type="button"
          >
            <i class="fa-solid fa-code me-2"></i>Embed &amp; Integration Generator
          </button>
        </li>
      </ul>

      <!-- ── TAB 1: BUG REPORTS & TRIAGE ── -->
      <div v-show="activeTab === 'reports'" class="tab-pane fade show active">
        <!-- Filter Toolbar Card -->
        <div class="card border-0 shadow-sm bg-white mb-4 filter-toolbar-card">
          <div class="card-body p-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
            <!-- Status & Severity Segmented Pills -->
            <div class="d-flex align-items-center gap-3 flex-wrap">
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <span class="text-muted-custom text-xs fw-bold text-uppercase me-1">Status:</span>
                <div class="btn-group category-pills-group p-1 rounded-3" role="group">
                  <button 
                    v-for="st in statusFilters" 
                    :key="st.value" 
                    @click="selectedStatusFilter = st.value" 
                    class="btn btn-xs fw-bold px-3 py-1 rounded-2 category-filter-pill"
                    :class="{ 'active-filter': selectedStatusFilter === st.value }"
                  >
                    <span>{{ st.label }}</span>
                  </button>
                </div>
              </div>

              <div class="d-flex align-items-center gap-2 flex-wrap">
                <span class="text-muted-custom text-xs fw-bold text-uppercase me-1">Severity:</span>
                <div class="btn-group category-pills-group p-1 rounded-3" role="group">
                  <button 
                    v-for="sev in severityFilters" 
                    :key="sev.value" 
                    @click="selectedSeverityFilter = sev.value" 
                    class="btn btn-xs fw-bold px-2 py-1 rounded-2 category-filter-pill"
                    :class="{ 'active-filter': selectedSeverityFilter === sev.value }"
                  >
                    <span>{{ sev.label }}</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Type & Search Filters -->
            <div class="d-flex align-items-center gap-2 flex-grow-1 justify-content-end flex-wrap" style="max-width: 440px;">
              <select class="form-select form-select-sm bg-light border-0 text-xs fw-semibold" v-model="selectedTypeFilter" style="width: 135px;">
                <option value="">All Types</option>
                <option value="bug">Bug Report</option>
                <option value="feedback">Feedback</option>
                <option value="ui">UI / UX Issue</option>
              </select>

              <div class="position-relative flex-grow-1" style="min-width: 200px;">
                <i class="fa-solid fa-magnifying-glass text-muted-custom search-icon"></i>
                <input 
                  type="text" 
                  class="form-control form-control-sm ps-4 pe-4 bg-light border-0 focus-glow text-xs" 
                  v-model="bugSearchQuery" 
                  placeholder="Search bug title, description, reporter..." 
                />
                <button 
                  v-if="bugSearchQuery" 
                  @click="bugSearchQuery = ''" 
                  class="btn btn-link btn-xs text-muted-custom position-absolute end-0 top-50 translate-middle-y me-1 p-0 text-decoration-none"
                >
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoadingBugReports" class="text-center py-5">
          <div class="spinner-border text-info" role="status" style="width: 2.5rem; height: 2.5rem;">
            <span class="visually-hidden">Loading bug reports...</span>
          </div>
          <p class="text-muted-custom text-xs mt-3 fw-semibold">Fetching reported issues...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredBugReports.length === 0" class="card border-0 shadow-sm bg-white p-5 text-center my-4">
          <i class="fa-solid fa-circle-check fa-3x text-success mb-3 opacity-75"></i>
          <h5 class="fw-bold text-dark-blue mb-1">No Bug Reports Found</h5>
          <p class="text-muted-custom text-xs mb-4" style="max-width: 420px; margin: 0 auto;">
            {{ bugSearchQuery || selectedStatusFilter || selectedTypeFilter || selectedSeverityFilter ? 'No reports match your current filter criteria.' : 'Great job! There are no bug reports logged for ' + (currentPlatform ? currentPlatform.name : 'this platform') + '.' }}
          </p>
          <div class="d-flex justify-content-center gap-2">
            <button class="btn btn-cyan-primary btn-sm fw-bold px-3 py-2" @click="openSandboxModal">
              <i class="fa-solid fa-vial me-1"></i> Launch Widget Sandbox &amp; Test Submit
            </button>
          </div>
        </div>

        <!-- Bug Reports Feed -->
        <div v-else class="d-flex flex-column gap-3">
          <div 
            v-for="report in filteredBugReports" 
            :key="report.id" 
            class="card border-0 shadow-sm bg-white changelog-card bug-report-card overflow-hidden"
          >
            <div class="card-header bg-white border-bottom px-3 py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <!-- Severity Badge -->
                <span class="badge text-uppercase font-monospace text-xxs px-2 py-1 rounded-pill" :class="getSeverityBadgeClass(report.severity)">
                  <i class="fa-solid fa-triangle-exclamation me-1"></i> {{ report.severity || 'medium' }}
                </span>

                <!-- Issue Type Badge -->
                <span class="badge bg-light text-dark-blue border text-xxs font-monospace px-2 py-1">
                  <i class="fa-solid fa-tag me-1 text-info"></i> {{ formatIssueType(report.issue_type) }}
                </span>

                <!-- Report UUID -->
                <span class="badge bg-purple-subtle text-purple border border-purple-subtle text-xxs font-monospace px-2 py-1">
                  {{ report.report_uuid || ('REPT-' + report.id) }}
                </span>

                <!-- Jira Linked Badge -->
                <a
                  v-if="report.jira_issue_key"
                  :href="report.jira_issue_url || ('https://' + (widgetForm.jira_domain || 'vertex.atlassian.net') + '/browse/' + report.jira_issue_key)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="badge bg-primary-subtle text-primary border border-primary-subtle text-xxs font-monospace px-2 py-1 text-decoration-none d-inline-flex align-items-center gap-1"
                  title="Open Issue in Jira Cloud"
                >
                  <i class="fa-solid fa-ticket"></i>
                  <span>{{ report.jira_issue_key }}</span>
                  <i class="fa-solid fa-arrow-up-right-from-square" style="font-size: 9px;"></i>
                </a>

                <!-- Workflow Stage / AI Auto-Fix Badge -->
                <span
                  v-if="report.workflow_stage || (report.ai_status && report.ai_status !== 'none')"
                  class="badge text-xxs font-monospace px-2 py-1 d-inline-flex align-items-center gap-1"
                  :class="getWorkflowStageBadgeClass(report)"
                  @click="openTriageModal(report)"
                  style="cursor: pointer;"
                  title="Click to view AI Auto-Fix & SDLC Pipeline details"
                >
                  <i :class="getWorkflowStageIcon(report)"></i>
                  <span>{{ getWorkflowStageLabel(report.workflow_stage, report.ai_status, report.ai_verdict) }}</span>
                </span>

                <h5 class="fw-bold mb-0 text-dark-blue ms-1">{{ report.title }}</h5>
              </div>

              <!-- Status Dropdown & Jira Push Action -->
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <span class="text-muted-custom text-xs me-1 d-none d-sm-inline">
                  <i class="fa-solid fa-clock me-1"></i> {{ formatDate(report.created_at) }}
                </span>

                <button
                  v-if="!report.jira_issue_key"
                  class="btn btn-outline-primary btn-xs px-3 py-1 fw-semibold d-inline-flex align-items-center gap-1"
                  @click="pushReportToJira(report)"
                  :disabled="pushingJiraId === report.id || isSubmitting"
                  title="Push this bug report to Jira project"
                >
                  <span v-if="pushingJiraId === report.id" class="spinner-border spinner-border-sm" style="width: 12px; height: 12px;"></span>
                  <i v-else class="fa-solid fa-ticket"></i>
                  <span>{{ pushingJiraId === report.id ? 'Pushing...' : 'Push to Jira' }}</span>
                </button>
                <div v-else class="btn-group btn-group-sm" role="group">
                  <a
                    :href="report.jira_issue_url || ('https://' + (widgetForm.jira_domain || 'vertex.atlassian.net') + '/browse/' + report.jira_issue_key)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="btn btn-primary btn-xs px-2 py-1 fw-semibold d-inline-flex align-items-center gap-1 text-white text-decoration-none"
                    title="View synced ticket in Jira"
                  >
                    <i class="fa-solid fa-ticket"></i>
                    <span>{{ report.jira_issue_key }}</span>
                    <i class="fa-solid fa-arrow-up-right-from-square ms-1" style="font-size: 9px;"></i>
                  </a>
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-xs px-2 py-1 d-inline-flex align-items-center"
                    @click="pushReportToJira(report, true)"
                    :disabled="pushingJiraId === report.id || isSubmitting"
                    title="Re-Push / Recreate Jira Ticket (if deleted or needs fresh ticket)"
                  >
                    <span v-if="pushingJiraId === report.id" class="spinner-border spinner-border-sm" style="width: 10px; height: 10px;"></span>
                    <i v-else class="fa-solid fa-arrows-rotate" style="font-size: 10px;"></i>
                  </button>
                </div>

                <!-- AI Auto-Fix Quick Trigger Button (Temporarily Parked / Disabled) -->
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-xs px-2.5 py-1 fw-bold d-inline-flex align-items-center gap-1 opacity-50"
                  disabled
                  title="AI Auto-Fix is temporarily parked"
                >
                  <i class="fa-solid fa-wand-magic-sparkles"></i>
                  <span>Auto-Fix</span>
                </button>

                <select 
                  class="form-select form-select-sm border text-xs fw-bold rounded-2 px-2 py-1"
                  :class="getStatusSelectClass(report.status)"
                  :value="report.status || 'open'"
                  @change="updateReportStatus(report, $event.target.value)"
                  :disabled="isSubmitting"
                  style="width: 140px;"
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>

                <button 
                  class="btn btn-outline-secondary btn-xs px-3 py-1 fw-semibold"
                  @click="openTriageModal(report)"
                  title="Open SDLC Pipeline, AI Fix & Triage Modal"
                >
                  <i class="fa-solid fa-pen-to-square me-1 text-info"></i> Pipeline &amp; Triage
                </button>

                <button 
                  class="btn btn-outline-danger btn-xs px-2 py-1 delete-btn"
                  @click="confirmDeleteReport(report)"
                  title="Delete Report"
                  :disabled="isSubmitting"
                >
                  <i class="fa-solid fa-trash-can"></i>
                </button>
              </div>
            </div>

            <div class="card-body p-4">
              <!-- Description -->
              <p class="text-dark-blue text-xs mb-3" style="line-height: 1.6; white-space: pre-line;">{{ report.description }}</p>

              <!-- Reporter info -->
              <div class="d-flex align-items-center gap-3 text-muted-custom text-xs mb-3 flex-wrap">
                <span><i class="fa-regular fa-user me-1 text-info"></i> Reporter: <strong class="text-dark-blue">{{ report.reporter_name || 'Anonymous' }}</strong></span>
                <span v-if="report.reporter_email"><i class="fa-regular fa-envelope me-1 text-info"></i> {{ report.reporter_email }}</span>
              </div>

              <!-- AI Auto-Fix & SDLC Summary Banner (if active or fixed) -->
              <div
                v-if="(report.ai_status && report.ai_status !== 'none') || report.ai_analysis || report.ai_branch"
                class="p-3 mb-3 rounded-3 border bg-cyan-subtle border-cyan-subtle d-flex align-items-start justify-content-between flex-wrap gap-2"
              >
                <div class="d-flex align-items-start gap-2" style="max-width: 78%;">
                  <i class="fa-solid fa-robot text-cyan-primary mt-1"></i>
                  <div class="text-xs">
                    <div class="d-flex align-items-center flex-wrap gap-2 mb-1">
                      <strong class="text-dark-blue">AI Triage &amp; Auto-Fix Pipeline:</strong>
                      <span class="badge bg-white text-dark-blue border font-monospace text-xxs">
                        Stage: {{ getWorkflowStageLabel(report.workflow_stage, report.ai_status, report.ai_verdict) }}
                      </span>
                      <span v-if="report.ai_verdict === 'legit'" class="badge bg-success text-white text-xxs">
                        <i class="fa-solid fa-check me-1"></i>Legit Bug
                      </span>
                      <span v-else-if="report.ai_verdict === 'not_legit'" class="badge bg-secondary text-white text-xxs">
                        Not a Code Bug
                      </span>
                      <span v-if="report.ai_branch" class="badge bg-white text-primary border font-monospace text-xxs">
                        <i class="fa-solid fa-code-branch me-1"></i>{{ report.ai_branch }}
                      </span>
                      <span v-if="report.ai_commit_hash" class="badge bg-white text-secondary border font-monospace text-xxs">
                        <i class="fa-solid fa-code-commit me-1"></i>{{ report.ai_commit_hash }}
                      </span>
                    </div>
                    <div v-if="report.ai_analysis" class="text-muted-custom" style="white-space: pre-line;">{{ report.ai_analysis }}</div>
                    <div v-else-if="report.ai_status === 'queued'" class="text-muted-custom">
                      Queued for Antigravity AI triage &amp; local dev fix. Click <strong>Pipeline &amp; Triage</strong> to copy the AI prompt or update Jira stage.
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn-white bg-white border btn-xs fw-bold text-dark-blue px-2 py-1 shadow-xs"
                  @click="openTriageModal(report)"
                >
                  <i class="fa-solid fa-sliders me-1 text-info"></i> Manage Stage &amp; Jira
                </button>
              </div>

              <!-- Technical Metadata Strip -->
              <div class="p-3 bg-light rounded-3 border d-flex align-items-center justify-content-between flex-wrap gap-2 text-xs mb-3">
                <div class="d-flex align-items-center gap-3 flex-wrap overflow-hidden">
                  <div class="d-flex align-items-center gap-1 text-truncate" style="max-width: 400px;">
                    <i class="fa-solid fa-link text-info me-1"></i>
                    <span class="text-muted-custom fw-semibold me-1">Page URL:</span>
                    <a :href="safePageUrl(report.page_url)" target="_blank" class="text-cyan-primary text-decoration-none fw-semibold text-truncate">{{ report.page_url || 'N/A' }}</a>
                  </div>
                  <div class="d-flex align-items-center gap-1">
                    <i class="fa-solid fa-desktop text-secondary me-1"></i>
                    <span class="text-muted-custom fw-semibold me-1">Browser / UA:</span>
                    <span class="text-dark-blue text-truncate" style="max-width: 260px;">{{ report.user_agent || 'Standard Web Browser' }}</span>
                  </div>
                </div>
                <div>
                  <span class="badge bg-white text-dark-blue border text-xxs font-monospace px-2 py-1">
                    <i class="fa-solid fa-expand me-1 text-info"></i> {{ report.viewport || '1920x1080' }}
                  </span>
                </div>
              </div>

              <!-- Media Attachments Row -->
              <div v-if="report.screenshot_url || report.screen_recording_url || report.screenshot_data || report.admin_notes" class="d-flex align-items-center gap-3 flex-wrap pt-3 border-top">
                <!-- Screenshot Thumbnail -->
                <div v-if="report.screenshot_url || report.screenshot_data" class="screenshot-thumb-wrapper border rounded-3 overflow-hidden position-relative" style="width: 160px; height: 100px; cursor: pointer;" @click="openScreenshotLightbox(report)">
                  <img :src="report.screenshot_url || report.screenshot_data" class="w-100 h-100 object-fit-cover" alt="Captured Screenshot" />
                  <div class="screenshot-thumb-overlay d-flex align-items-center justify-content-center text-white text-xs fw-bold">
                    <i class="fa-solid fa-magnifying-glass-plus me-1"></i> View Screenshot
                  </div>
                </div>

                <!-- Screen Recording Video Player -->
                <div v-if="report.screen_recording_url || report.screen_recording_data" class="recording-thumb-wrapper border rounded-3 p-2 bg-dark text-white d-flex align-items-center gap-3" style="min-width: 280px;">
                  <i class="fa-solid fa-circle-play text-danger fa-2x ms-2"></i>
                  <div>
                    <div class="fw-bold text-xs text-white">Screen Recording Captured</div>
                    <button class="btn btn-cyan-primary btn-xs mt-1 px-2 py-1 fw-bold" @click="openVideoModal(report)">
                      <i class="fa-solid fa-play me-1"></i> Watch Video Clip
                    </button>
                  </div>
                </div>

                <!-- Admin Triage Notes Badge if present -->
                <div v-if="report.admin_notes" class="ms-auto p-2 bg-cyan-subtle rounded-2 border border-cyan-subtle text-xs text-dark-blue" style="max-width: 340px;">
                  <strong><i class="fa-solid fa-clipboard-check me-1 text-cyan-primary"></i> Admin Note:</strong>
                  <span class="ms-1 text-muted-custom">{{ report.admin_notes }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── TAB 2: WIDGET CUSTOMIZER & LIVE SANDBOX ── -->
      <div v-show="activeTab === 'customizer'" class="tab-pane fade show active">
        <div class="row g-4">
          <!-- Left Column: Widget Configuration Form -->
          <div class="col-lg-5">
            <div class="card border-0 shadow-sm bg-white h-100 filter-toolbar-card">
              <div class="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
                <h6 class="fw-bold mb-0 text-dark-blue">
                  <i class="fa-solid fa-sliders text-info me-2"></i> Widget Orientation &amp; Brand Customizer
                </h6>
                <span class="badge bg-cyan-subtle text-cyan-primary border border-cyan-subtle text-xxs">Live Sync</span>
              </div>
              <div class="card-body p-4">
                <form @submit.prevent="saveWidgetSettings">
                  <!-- Platform Identifiers Card -->
                  <div class="p-3 bg-light rounded-3 mb-4 border">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-0">
                        <i class="fa-solid fa-fingerprint text-info me-1"></i> Platform Identifiers
                      </label>
                      <span class="badge bg-white text-dark-blue border font-monospace text-xxs">ID: #{{ currentPlatform ? currentPlatform.id : 0 }}</span>
                    </div>

                    <div class="row g-2 mb-2">
                      <div class="col-5">
                        <label class="text-muted-custom text-xxs fw-semibold d-block mb-1">Platform Key (Slug)</label>
                        <div class="input-group input-group-sm">
                          <input type="text" class="form-control bg-white font-monospace text-xs fw-bold" :value="currentPlatform ? currentPlatform.key : ''" readonly />
                          <button type="button" class="btn btn-outline-secondary px-2" @click="copyText(currentPlatform ? currentPlatform.key : '')" title="Copy Platform Key">
                            <i class="fa-regular fa-copy"></i>
                          </button>
                        </div>
                      </div>
                      <div class="col-7">
                        <label class="text-muted-custom text-xxs fw-semibold d-block mb-1">Widget Security UUID</label>
                        <div class="input-group input-group-sm">
                          <input type="text" class="form-control bg-white font-monospace text-xs" :value="widgetForm.widget_uuid" readonly />
                          <button type="button" class="btn btn-outline-secondary px-2" @click="copyText(widgetForm.widget_uuid)" title="Copy Widget UUID">
                            <i class="fa-regular fa-copy"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="text-muted-custom text-xxs d-flex align-items-center gap-1 mt-1">
                      <i class="fa-solid fa-circle-info text-info"></i>
                      <span>Authenticate public widget submissions using this Platform Key &amp; UUID.</span>
                    </div>
                  </div>

                  <!-- Widget Orientation / Dock Position Selector (2-Column Visual Grid with Mini Screen Diagrams) -->
                  <div class="mb-4">
                    <label class="form-label fw-bold text-secondary text-xs text-uppercase mb-2 d-flex justify-content-between align-items-center">
                      <span>Widget Orientation / Dock Position</span>
                      <span class="badge bg-cyan-subtle text-cyan-primary text-xxs fw-bold">Recommended: Right Edge</span>
                    </label>
                    <div class="row g-2">
                      <div 
                        v-for="pos in positionOptions" 
                        :key="pos.value" 
                        :class="pos.value === 'right-edge' ? 'col-12' : 'col-sm-6'"
                      >
                        <div 
                          class="position-radio-card d-flex align-items-center justify-content-between h-100"
                          :class="{ 'active-pos-card': widgetForm.widget_position === pos.value }"
                          @click="widgetForm.widget_position = pos.value"
                        >
                          <div class="d-flex align-items-center gap-3">
                            <!-- Mini CSS Screen Position Diagram -->
                            <div class="mini-screen-diagram flex-shrink-0">
                              <div class="mini-screen-topbar">
                                <span></span><span></span><span></span>
                              </div>
                              <div 
                                class="mini-screen-indicator" 
                                :class="'indicator-' + pos.value"
                                :style="{ backgroundColor: widgetForm.primary_color || '#00a0c1' }"
                              ></div>
                            </div>
                            <div>
                              <div class="fw-bold text-xs text-dark-blue d-flex align-items-center gap-1">
                                <span>{{ pos.label }}</span>
                              </div>
                              <div class="text-muted-custom text-xxs" style="line-height: 1.3;">{{ pos.desc }}</div>
                            </div>
                          </div>
                          <div class="ms-2 flex-shrink-0">
                            <i 
                              v-if="widgetForm.widget_position === pos.value" 
                              class="fa-solid fa-circle-check text-cyan-primary fs-6"
                            ></i>
                            <i v-else class="fa-regular fa-circle text-muted opacity-50 fs-6"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Button Label & Theme Row -->
                  <div class="row g-3 mb-3">
                    <div class="col-sm-7">
                      <label class="form-label fw-bold text-secondary text-xs text-uppercase mb-1">Button Label Text</label>
                      <input type="text" class="form-control form-control-sm text-xs" v-model="widgetForm.widget_button_text" placeholder="e.g. Report a Bug" required />
                    </div>
                    <div class="col-sm-5">
                      <label class="form-label fw-bold text-secondary text-xs text-uppercase mb-1">Widget Theme</label>
                      <select class="form-select form-select-sm text-xs" v-model="widgetForm.widget_theme">
                        <option value="light">Light Surface</option>
                        <option value="dark">Dark Slate</option>
                      </select>
                    </div>
                  </div>

                  <!-- Brand Accent Color + Quick Preset Swatches -->
                  <div class="mb-3">
                    <label class="form-label fw-bold text-secondary text-xs text-uppercase mb-1">Brand Accent Color</label>
                    <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 p-2 bg-light rounded-3 border">
                      <div class="d-flex align-items-center gap-2">
                        <input type="color" class="form-control form-control-color border-0 p-0 bg-transparent" v-model="widgetForm.primary_color" style="width: 32px; height: 32px; cursor: pointer;" />
                        <input type="text" class="form-control form-control-sm text-xs font-monospace bg-white" v-model="widgetForm.primary_color" style="width: 95px;" />
                      </div>
                      <div class="d-flex align-items-center gap-1 flex-wrap">
                        <button
                          v-for="preset in colorPresets"
                          :key="preset"
                          type="button"
                          class="color-preset-btn rounded-circle border-0"
                          :class="{ active: (widgetForm.primary_color || '').toLowerCase() === preset.toLowerCase() }"
                          :style="{ backgroundColor: preset }"
                          @click="widgetForm.primary_color = preset"
                          :title="'Select ' + preset"
                        ></button>
                      </div>
                    </div>
                  </div>

                  <!-- Active Toggle -->
                  <div class="form-check form-switch mb-3 p-3 bg-light rounded-3 border d-flex align-items-center justify-content-between ms-0">
                    <div>
                      <label class="form-check-label fw-bold text-xs text-dark-blue d-block" for="widgetActiveSwitch">
                        Enable Floating Bug Widget
                      </label>
                      <span class="text-muted-custom text-xxs">Show floating trigger on host application</span>
                    </div>
                    <input class="form-check-input mt-0 ms-2" type="checkbox" role="switch" id="widgetActiveSwitch" v-model="widgetForm.widget_enabled" style="width: 2.5em; height: 1.3em; cursor: pointer;" />
                  </div>

                  <!-- Jira Cloud Integration Card -->
                  <div class="p-3 bg-light rounded-3 border mb-3">
                    <div class="d-flex align-items-center justify-content-between mb-2">
                      <span class="fw-bold text-xs text-dark-blue d-flex align-items-center gap-1.5">
                        <i class="fa-solid fa-diagram-project text-info me-1"></i> Jira Board Routing
                      </span>
                      <a
                        :href="'https://' + (widgetForm.jira_domain || 'vertex.atlassian.net') + '/jira/core/projects/' + (widgetForm.jira_project_key || 'DEV') + '/board?filter=&groupBy=none'"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-xxs fw-bold text-primary text-decoration-none d-inline-flex align-items-center gap-1"
                      >
                        <span>Open {{ widgetForm.jira_project_key || 'DEV' }} Board</span>
                        <i class="fa-solid fa-arrow-up-right-from-square" style="font-size: 9px;"></i>
                      </a>
                    </div>

                    <div class="row g-2 mb-2">
                      <div class="col-4">
                        <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Project Key</label>
                        <input type="text" class="form-control form-control-sm text-xs bg-white font-monospace text-uppercase fw-bold" v-model="widgetForm.jira_project_key" placeholder="DEV" />
                      </div>
                      <div class="col-8">
                        <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Jira Cloud Domain</label>
                        <input type="text" class="form-control form-control-sm text-xs bg-white font-monospace" v-model="widgetForm.jira_domain" placeholder="vertex.atlassian.net" />
                      </div>
                    </div>

                    <div class="form-check form-switch pt-2 border-top d-flex align-items-center justify-content-between ms-0 mb-0">
                      <div>
                        <label class="form-check-label fw-bold text-xs text-dark-blue d-block" for="jiraAutoSyncSwitch">
                          Auto-Push New Bug Reports to Jira
                        </label>
                        <span class="text-muted-custom text-xxs">
                          {{ widgetForm.jira_auto_sync ? 'Automatic: New reports create Jira tickets immediately' : 'Manual Mode: Use "Push to Jira" button on each bug report' }}
                        </span>
                      </div>
                      <input class="form-check-input mt-0 ms-2" type="checkbox" role="switch" id="jiraAutoSyncSwitch" v-model="widgetForm.jira_auto_sync" style="width: 2.5em; height: 1.3em; cursor: pointer;" />
                    </div>
                  </div>

                  <!-- Bitbucket Repositories Card -->
                  <div class="p-3 bg-light rounded-3 border mb-3">
                    <div class="d-flex align-items-center justify-content-between flex-wrap gap-1 mb-2">
                      <span class="fw-bold text-xs text-dark-blue">
                        <i class="fa-solid fa-code-branch text-info me-1"></i> Bitbucket Repositories
                      </span>
                      <div class="d-flex align-items-center gap-1.5">
                        <span class="badge bg-white text-secondary border font-monospace text-xxs">
                          {{ (widgetForm.repositories || []).length }} Attached &bull; {{ bitbucketRepoOptions.length }} Available
                        </span>
                        <button
                          type="button"
                          class="btn btn-white bg-white border btn-xs px-1.5 py-0.5 text-info"
                          @click="fetchBitbucketRepositories()"
                          title="Refresh repositories from Bitbucket Cloud"
                        >
                          <i class="fa-solid fa-arrows-rotate" :class="{ 'fa-spin': isLoadingBitbucketRepos }" style="font-size: 10px;"></i>
                        </button>
                      </div>
                    </div>

                    <!-- Streamlined One-Step Repo Selector -->
                    <div class="d-flex align-items-center gap-1.5 mb-2">
                      <select
                        class="form-select form-select-sm text-xs font-monospace bg-white flex-grow-1"
                        v-model="repoPicker.widget.selectedSlug"
                        @change="onRepoDropdownSelect('widget')"
                      >
                        <option value="">+ Attach a Bitbucket repository ({{ bitbucketRepoOptions.length }} available)...</option>
                        <option
                          v-for="opt in bitbucketRepoOptions"
                          :key="'w-repo-' + opt.repo_slug"
                          :value="opt.repo_slug"
                        >
                          {{ opt.repo_slug }}  —  {{ (opt.suggested_role || opt.role || 'repo').toUpperCase() }}{{ isRepoAttached('widget', opt.repo_slug) ? ' (Attached)' : '' }}
                        </option>
                        <option value="__custom__">+ Enter custom repository slug...</option>
                      </select>
                    </div>

                    <!-- Optional Custom Repo Slug Input -->
                    <div v-if="repoPicker.widget.selectedSlug === '__custom__'" class="d-flex align-items-center gap-1.5 mb-2">
                      <input
                        type="text"
                        class="form-control form-control-sm text-xs font-monospace bg-white flex-grow-1"
                        v-model="repoPicker.widget.customSlug"
                        placeholder="Enter Bitbucket repo slug (e.g. xsurflog_be)"
                        @keydown.enter.prevent="addRepositoryToTarget('widget')"
                      />
                      <button
                        type="button"
                        class="btn btn-cyan-primary btn-xs px-2.5 py-1 fw-bold flex-shrink-0"
                        @click="addRepositoryToTarget('widget')"
                        :disabled="!repoPicker.widget.customSlug"
                      >
                        <i class="fa-solid fa-plus me-1"></i>Attach
                      </button>
                    </div>

                    <!-- Attached Repositories List -->
                    <div v-if="widgetForm.repositories && widgetForm.repositories.length > 0" class="d-flex flex-column gap-1.5">
                      <div
                        v-for="(repo, rIdx) in widgetForm.repositories"
                        :key="'w-attached-' + rIdx + '-' + repo.repo_slug"
                        class="d-flex align-items-center justify-content-between px-2.5 py-1.5 rounded-2 bg-white border text-xxs repo-attached-row"
                      >
                        <div class="d-flex align-items-center gap-2 text-truncate min-w-0">
                          <select
                            class="form-select form-select-sm repo-role-badge-select fw-bold text-uppercase font-monospace"
                            v-model="repo.role"
                            title="Change repository role"
                          >
                            <option v-for="rOpt in repoRoleOptions" :key="'w-r-' + rOpt.value" :value="rOpt.value">
                              {{ rOpt.shortLabel || rOpt.value.toUpperCase() }}
                            </option>
                          </select>
                          <span class="font-monospace fw-bold text-dark-blue text-truncate">
                            {{ repo.workspace || widgetForm.bitbucket_workspace || 'VertexSecurity' }}/{{ repo.repo_slug }}
                          </span>
                        </div>
                        <div class="d-flex align-items-center gap-2 flex-shrink-0 ms-2">
                          <span class="badge bg-light text-secondary border font-monospace">
                            <i class="fa-solid fa-code-branch me-1 text-info"></i>{{ repo.branch || 'dev' }}
                          </span>
                          <button
                            type="button"
                            class="btn btn-link btn-xs text-muted hover-danger p-0"
                            @click="removeRepositoryFromTarget('widget', rIdx)"
                            title="Remove repository"
                          >
                            <i class="fa-solid fa-xmark"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-xxs text-muted-custom px-2.5 py-2 bg-white rounded-2 border text-center">
                      No repositories attached yet. Select a repository from the dropdown above to attach it.
                    </div>
                  </div>

                  <!-- Regenerate UUID option -->
                  <div class="mb-4 form-check">
                    <input type="checkbox" class="form-check-input" id="regenUuidCheck" v-model="widgetForm.regenerate_uuid" />
                    <label class="form-check-label text-xs text-muted-custom" for="regenUuidCheck">
                      Regenerate Widget UUID (Invalidates previous embed security tokens)
                    </label>
                  </div>

                  <button 
                    type="submit" 
                    class="btn btn-cyan-primary w-100 fw-bold py-2 text-xs d-flex align-items-center justify-content-center gap-2"
                    :disabled="isSubmitting"
                  >
                    <span v-if="isSubmitting" class="spinner-border spinner-border-sm"></span>
                    <i v-else class="fa-solid fa-check"></i>
                    <span>Save Widget Configuration</span>
                  </button>
                </form>
              </div>
            </div>
          </div>

          <!-- Right Column: Interactive Live Sandbox Viewport -->
          <div class="col-lg-7">
            <div class="card border-0 shadow-sm bg-white h-100 filter-toolbar-card">
              <div class="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div class="d-flex align-items-center gap-2">
                  <h6 class="fw-bold mb-0 text-dark-blue">
                    <i class="fa-solid fa-desktop text-info me-2"></i> Live Interactive Sandbox Preview
                  </h6>
                  <span class="badge bg-success-subtle text-success border border-success-subtle text-xxs fw-bold">Interactive</span>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <button 
                    type="button" 
                    class="btn btn-cyan-outline btn-xs fw-bold px-2 py-1 rounded-pill"
                    @click="showInlinePopoverPreview = !showInlinePopoverPreview"
                  >
                    <i class="fa-solid fa-window-restore me-1"></i>
                    {{ showInlinePopoverPreview ? 'Hide Popover Preview' : 'Toggle Popover Preview' }}
                  </button>
                  <button 
                    type="button" 
                    class="btn btn-cyan-primary btn-xs fw-bold px-2 py-1 rounded-pill"
                    @click="openSandboxModal"
                  >
                    <i class="fa-solid fa-expand me-1"></i> Full Capture Modal
                  </button>
                </div>
              </div>

              <div class="card-body p-3 d-flex flex-column">
                <!-- Sandbox Viewport Screen -->
                <div class="sandbox-viewport-frame border rounded-3 position-relative bg-light overflow-hidden shadow-xs flex-grow-1" style="min-height: 540px; display: flex; flex-direction: column;">
                  <!-- Mock Host Platform Header Bar -->
                  <div class="mock-browser-bar bg-dark text-white py-2 px-3 d-flex align-items-center justify-content-between text-xs">
                    <div class="d-flex align-items-center gap-2">
                      <span class="mock-dot bg-danger rounded-circle" style="width: 10px; height: 10px; display: inline-block;"></span>
                      <span class="mock-dot bg-warning rounded-circle" style="width: 10px; height: 10px; display: inline-block;"></span>
                      <span class="mock-dot bg-success rounded-circle" style="width: 10px; height: 10px; display: inline-block;"></span>
                      <div class="ms-2 px-3 py-1 rounded bg-secondary bg-opacity-25 font-monospace text-light text-xxs d-flex align-items-center gap-2">
                        <i class="fa-solid fa-lock text-success" style="font-size: 9px;"></i>
                        <span>https://app.{{ selectedPlatformKey || 'yourplatform' }}.io/dashboard</span>
                      </div>
                    </div>
                    <div class="d-flex align-items-center gap-2">
                      <span class="badge bg-secondary font-monospace text-xxs">{{ getPositionLabel(widgetForm.widget_position) }}</span>
                    </div>
                  </div>

                  <!-- Mock SaaS Platform Content -->
                  <div class="mock-page-content sandbox-viewport-body p-4 flex-grow-1 position-relative overflow-hidden" style="background-color: #f8fafc;">
                    <!-- Mock App Navbar -->
                    <div class="d-flex align-items-center justify-content-between mb-3 p-3 bg-white rounded-3 border shadow-xs">
                      <div class="d-flex align-items-center gap-2">
                        <div 
                          class="rounded-2 d-flex align-items-center justify-content-center"
                          style="width: 32px; height: 32px;"
                          :style="{ backgroundColor: hexToRgba(widgetForm.primary_color || '#00a0c1', 0.12), color: widgetForm.primary_color || '#00a0c1' }"
                        >
                          <img v-if="currentPlatform && isUrl(currentPlatform.icon)" :src="currentPlatform.icon" style="width: 20px; height: 20px; object-fit: contain;" alt="" />
                          <i v-else :class="currentPlatform ? currentPlatform.icon : 'fa-solid fa-layer-group'"></i>
                        </div>
                        <div>
                          <strong class="text-dark-blue text-xs d-block">{{ currentPlatform ? currentPlatform.name : 'Target Host Platform' }}</strong>
                          <span class="text-muted-custom text-xxs">Production Environment &bull; v2.4.0</span>
                        </div>
                      </div>
                      <span class="badge bg-light text-secondary border font-monospace text-xxs">Click Docked Trigger to Slide Drawer</span>
                    </div>

                    <!-- Mock SaaS KPI Cards -->
                    <div class="row g-3 mb-3">
                      <div class="col-4">
                        <div class="p-3 bg-white rounded-3 border shadow-xs">
                          <div class="text-muted-custom text-xxs fw-bold text-uppercase mb-1">Active Sessions</div>
                          <div class="fw-bold text-dark-blue fs-5">1,428</div>
                          <span class="text-success text-xxs fw-semibold"><i class="fa-solid fa-arrow-trend-up me-1"></i>+12.4%</span>
                        </div>
                      </div>
                      <div class="col-4">
                        <div class="p-3 bg-white rounded-3 border shadow-xs">
                          <div class="text-muted-custom text-xxs fw-bold text-uppercase mb-1">API Latency</div>
                          <div class="fw-bold text-dark-blue fs-5">42ms</div>
                          <span class="text-info text-xxs fw-semibold"><i class="fa-solid fa-bolt me-1"></i>Healthy</span>
                        </div>
                      </div>
                      <div class="col-4">
                        <div class="p-3 bg-white rounded-3 border shadow-xs">
                          <div class="text-muted-custom text-xxs fw-bold text-uppercase mb-1">Widget Status</div>
                          <div class="fw-bold text-dark-blue text-xs mt-1">
                            <span v-if="widgetForm.widget_enabled" class="badge bg-success-subtle text-success border border-success-subtle">Enabled</span>
                            <span v-else class="badge bg-secondary-subtle text-secondary border border-secondary-subtle">Disabled</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="p-3 bg-white rounded-3 border shadow-xs">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <h6 class="fw-bold text-dark-blue text-xs mb-0">Application Workspace Canvas</h6>
                        <span class="text-muted-custom text-xxs">Theme: <strong>{{ widgetForm.widget_theme === 'dark' ? 'Dark Slate' : 'Light Surface' }}</strong></span>
                      </div>
                      <p class="text-muted-custom text-xxs mb-2">
                        The floating bug widget trigger docks seamlessly at <strong>{{ getPositionLabel(widgetForm.widget_position) }}</strong>. Click the trigger tab or button inside this preview to slide the bug drawer open and closed!
                      </p>
                      <div class="progress" style="height: 6px;">
                        <div class="progress-bar" :style="{ width: '68%', backgroundColor: widgetForm.primary_color || '#00a0c1' }"></div>
                      </div>
                    </div>

                    <!-- ── UNIFIED SLIDING DRAWER ASSEMBLY IN SANDBOX (TRIGGER + POPOVER) ── -->
                    <div 
                      v-show="widgetForm.widget_enabled"
                      class="sandbox-drawer-unit"
                      :class="[
                        getWidgetPositionClass(widgetForm.widget_position),
                        { 'is-open': showInlinePopoverPreview }
                      ]"
                    >
                      <!-- FLOATING WIDGET TRIGGER (SLIDES TOGETHER WITH DRAWER) -->
                      <div 
                        class="sandbox-widget-trigger shadow"
                        :style="{ backgroundColor: widgetForm.primary_color || '#00a0c1' }"
                        @click="showInlinePopoverPreview = !showInlinePopoverPreview"
                      >
                        <i class="fa-solid fa-bug"></i>
                        <span>{{ widgetForm.widget_button_text || 'Report a Bug' }}</span>
                      </div>

                      <!-- INLINE MINIATURE POPOVER CARD PREVIEW -->
                      <div 
                        class="sandbox-inline-popover border"
                        :class="[
                          widgetForm.widget_theme === 'dark' ? 'sandbox-popover-dark' : 'sandbox-popover-light'
                        ]"
                      >
                        <div class="p-2 border-bottom d-flex justify-content-between align-items-center">
                          <div class="d-flex align-items-center gap-2">
                            <span 
                              class="rounded-2 d-inline-flex align-items-center justify-content-center"
                              style="width: 24px; height: 24px; font-size: 11px;"
                              :style="{ backgroundColor: hexToRgba(widgetForm.primary_color || '#00a0c1', 0.15), color: widgetForm.primary_color || '#00a0c1' }"
                            >
                              <i class="fa-solid fa-bug"></i>
                            </span>
                            <div>
                              <div class="fw-bold text-xs" style="line-height: 1.1;">{{ currentPlatform ? currentPlatform.name : 'Report an Issue' }}</div>
                              <div class="text-muted-custom" style="font-size: 9.5px;">Capture screenshot or video</div>
                            </div>
                          </div>
                          <button type="button" class="btn btn-link btn-xs text-muted-custom p-0" @click="showInlinePopoverPreview = false">
                            <i class="fa-solid fa-xmark"></i>
                          </button>
                        </div>
                        <div class="p-2">
                          <!-- Mini Segmented Bar -->
                          <div class="d-flex gap-1 p-1 rounded-2 mb-2" :class="widgetForm.widget_theme === 'dark' ? 'bg-dark' : 'bg-light'">
                            <span class="badge bg-white text-dark shadow-xs flex-fill text-center py-1" style="font-size: 9.5px;"><i class="fa-solid fa-bug text-danger me-1"></i>Bug</span>
                            <span class="text-muted-custom flex-fill text-center py-1" style="font-size: 9.5px;">Feedback</span>
                            <span class="text-muted-custom flex-fill text-center py-1" style="font-size: 9.5px;">UI / UX</span>
                          </div>
                          <!-- Mini Capture Tiles -->
                          <div class="d-flex gap-1 mb-2">
                            <div class="border rounded-2 p-1 text-center flex-fill" style="font-size: 9.5px;">
                              <i class="fa-solid fa-camera text-info d-block mb-1"></i>
                              <span class="fw-bold">Screenshot</span>
                            </div>
                            <div class="border rounded-2 p-1 text-center flex-fill" style="font-size: 9.5px;">
                              <i class="fa-solid fa-video text-danger d-block mb-1"></i>
                              <span class="fw-bold">Record Tab</span>
                            </div>
                            <div class="border rounded-2 p-1 text-center" style="font-size: 9.5px; width: 48px;">
                              <i class="fa-solid fa-paperclip text-secondary d-block mb-1"></i>
                              <span>Upload</span>
                            </div>
                          </div>
                          <button 
                            type="button" 
                            class="btn btn-xs w-100 text-white fw-bold py-1 rounded-2"
                            :style="{ backgroundColor: widgetForm.primary_color || '#00a0c1', fontSize: '10.5px' }"
                            @click="openSandboxModal"
                          >
                            <i class="fa-solid fa-paper-plane me-1"></i> Test Full Submission Drawer
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── TAB 3: EMBED & INTEGRATION GENERATOR ── -->
      <div v-show="activeTab === 'embed'" class="tab-pane fade show active">
        <div class="card border-0 shadow-sm bg-white mb-4 platform-banner-card">
          <div class="card-body p-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div class="d-flex align-items-center gap-3">
              <div class="platform-icon-avatar bg-cyan-subtle text-cyan-primary rounded-3">
                <i class="fa-solid fa-code fs-4"></i>
              </div>
              <div>
                <h5 class="fw-bold text-dark-blue mb-1">Embed Snippet Generator for {{ currentPlatform ? currentPlatform.name : 'Platform' }}</h5>
                <p class="text-muted-custom text-xs mb-0">Copy any of the integration snippets below to embed the bug reporting widget into external web apps.</p>
              </div>
            </div>
            <div class="d-flex align-items-center gap-2 font-monospace text-xs">
              <span class="badge bg-light text-dark-blue border px-3 py-2">UUID: {{ widgetForm.widget_uuid }}</span>
            </div>
          </div>
        </div>

        <div class="row g-4">
          <!-- Snippet 1: Lightweight Script Loader -->
          <div class="col-lg-6">
            <div class="card border-0 shadow-sm bg-white h-100 filter-toolbar-card">
              <div class="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
                <h6 class="fw-bold mb-0 text-dark-blue">
                  <i class="fa-solid fa-file-code text-info me-2"></i> 1. Script Widget Loader (Recommended)
                </h6>
                <button class="btn btn-cyan-outline btn-xs px-3 py-1 fw-bold" @click="copyCode(scriptSnippetCode)">
                  <i class="fa-regular fa-copy me-1"></i> Copy Snippet
                </button>
              </div>
              <div class="card-body p-3">
                <p class="text-muted-custom text-xs mb-2">Paste this single tag before the closing <code>&lt;/body&gt;</code> tag on host pages:</p>
                <pre class="bg-dark text-light p-3 rounded-3 text-xs mb-0 font-monospace overflow-auto" style="max-height: 160px;">{{ scriptSnippetCode }}</pre>
              </div>
            </div>
          </div>

          <!-- Snippet 2: Standalone Iframe Embed -->
          <div class="col-lg-6">
            <div class="card border-0 shadow-sm bg-white h-100 filter-toolbar-card">
              <div class="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
                <h6 class="fw-bold mb-0 text-dark-blue">
                  <i class="fa-solid fa-window-maximize text-info me-2"></i> 2. Standalone Iframe Embed
                </h6>
                <button class="btn btn-cyan-outline btn-xs px-3 py-1 fw-bold" @click="copyCode(iframeSnippetCode)">
                  <i class="fa-regular fa-copy me-1"></i> Copy Snippet
                </button>
              </div>
              <div class="card-body p-3">
                <p class="text-muted-custom text-xs mb-2">Embeds a full-page bug reporting workspace into an iframe:</p>
                <pre class="bg-dark text-light p-3 rounded-3 text-xs mb-0 font-monospace overflow-auto" style="max-height: 160px;">{{ iframeSnippetCode }}</pre>
              </div>
            </div>
          </div>

          <!-- Snippet 3: Direct REST API Submission -->
          <div class="col-12">
            <div class="card border-0 shadow-sm bg-white filter-toolbar-card">
              <div class="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
                <h6 class="fw-bold mb-0 text-dark-blue">
                  <i class="fa-solid fa-terminal text-info me-2"></i> 3. Direct REST API Submission (cURL / JS Fetch)
                </h6>
                <button class="btn btn-cyan-outline btn-xs px-3 py-1 fw-bold" @click="copyCode(curlSnippetCode)">
                  <i class="fa-regular fa-copy me-1"></i> Copy Snippet
                </button>
              </div>
              <div class="card-body p-3">
                <p class="text-muted-custom text-xs mb-2">Custom backend or headless API integration snippet:</p>
                <pre class="bg-dark text-light p-3 rounded-3 text-xs mb-0 font-monospace overflow-auto" style="max-height: 200px;">{{ curlSnippetCode }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <!-- ── MODALS ── -->
    <!-- ══════════════════════════════════════════════════════════════════════ -->

    <!-- REGISTER NEW PLATFORM MODAL -->
    <div class="modal fade" id="platformFormModal" tabindex="-1" aria-hidden="true" ref="platformModal">
      <div class="modal-dialog modal-dialog-scrollable custom-modal-scrollable" style="max-width: 620px; margin: 1.75rem auto;">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-white border-bottom sticky-top py-3 px-4">
            <h5 class="modal-title fw-bold text-dark-blue mb-0">
              <i class="fa-solid fa-layer-group text-info me-2"></i> Register New Platform
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="closeModalSafe('platformFormModal')"></button>
          </div>
          <div class="modal-body custom-modal-body p-4">
            <form @submit.prevent="saveNewPlatform">
              <!-- Basic Platform Identity -->
              <div class="row g-2.5 mb-2.5">
                <div class="col-md-7">
                  <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Platform Name *</label>
                  <input type="text" class="form-control form-control-sm" v-model="platformForm.name" @input="onPlatformNameInput" placeholder="e.g. SOC Dashboard, Helm Hub" required />
                </div>
                <div class="col-md-5">
                  <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Platform Key / Slug *</label>
                  <input type="text" class="form-control form-control-sm font-monospace" v-model="platformForm.key" placeholder="e.g. soc, helm, core" required />
                </div>
              </div>

              <div class="mb-2.5">
                <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Description</label>
                <textarea class="form-control form-control-sm" rows="2" v-model="platformForm.description" placeholder="Brief description of this platform service..."></textarea>
              </div>

              <!-- Compact Brand Logo & Theme Color Row -->
              <div class="row g-2.5 mb-3">
                <div class="col-md-5">
                  <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Brand Logo (Optional)</label>
                  <input type="file" accept="image/*" class="form-control form-control-sm text-xs" @change="onIconFileChange($event, 'create')" />
                </div>
                <div class="col-md-7">
                  <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Theme Accent Color</label>
                  <div class="d-flex align-items-center justify-content-between gap-2 px-2.5 py-1 bg-light rounded-2 border" style="min-height: 31px;">
                    <div class="d-flex align-items-center gap-1.5">
                      <input type="color" class="form-control form-control-color border-0 p-0 bg-transparent" v-model="platformForm.primary_color" style="width: 22px; height: 22px; cursor: pointer;" />
                      <input type="text" class="form-control form-control-sm text-xxs font-monospace bg-white py-0 px-1.5" v-model="platformForm.primary_color" style="width: 76px; height: 22px;" />
                    </div>
                    <div class="d-flex align-items-center gap-1">
                      <button
                        v-for="preset in colorPresets"
                        :key="'create-' + preset"
                        type="button"
                        class="color-preset-btn-sm rounded-circle border-0"
                        :class="{ active: (platformForm.primary_color || '').toLowerCase() === preset.toLowerCase() }"
                        :style="{ backgroundColor: preset }"
                        @click="platformForm.primary_color = preset"
                      ></button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Section 1: Jira Board Routing -->
              <div class="p-3 bg-light rounded-3 border mb-3">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <span class="fw-bold text-dark-blue text-xs">
                    <i class="fa-solid fa-diagram-project text-info me-1.5"></i> Jira Board Routing
                  </span>
                  <span class="badge bg-white text-secondary border font-monospace text-xxs">
                    Board: {{ (platformForm.jira_project_key || 'DEV').toUpperCase() }}
                  </span>
                </div>
                <div class="row g-2">
                  <div class="col-sm-4">
                    <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Board / Project Key *</label>
                    <input
                      type="text"
                      class="form-control form-control-sm font-monospace text-uppercase fw-bold bg-white"
                      v-model="platformForm.jira_project_key"
                      placeholder="DEV"
                    />
                  </div>
                  <div class="col-sm-8">
                    <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Jira Cloud Domain</label>
                    <input
                      type="text"
                      class="form-control form-control-sm font-monospace bg-white"
                      v-model="platformForm.jira_domain"
                      placeholder="vertex.atlassian.net"
                    />
                  </div>
                </div>
              </div>

              <!-- Section 2: Bitbucket Repositories -->
              <div class="p-3 bg-light rounded-3 border">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <span class="fw-bold text-dark-blue text-xs">
                    <i class="fa-solid fa-code-branch text-info me-1.5"></i> Bitbucket Repositories
                  </span>
                  <div class="d-flex align-items-center gap-1.5">
                    <span class="badge bg-white text-secondary border font-monospace text-xxs">
                      {{ (platformForm.repositories || []).length }} Attached &bull; {{ bitbucketRepoOptions.length }} Available
                    </span>
                    <button
                      type="button"
                      class="btn btn-white bg-white border btn-xs px-1.5 py-0.5 text-info"
                      @click="fetchBitbucketRepositories()"
                      title="Refresh repositories from Bitbucket Cloud"
                    >
                      <i class="fa-solid fa-arrows-rotate" :class="{ 'fa-spin': isLoadingBitbucketRepos }" style="font-size: 10px;"></i>
                    </button>
                  </div>
                </div>

                <!-- One-Step Repo Selector -->
                <div class="d-flex align-items-center gap-1.5 mb-2">
                  <select
                    class="form-select form-select-sm text-xs font-monospace bg-white flex-grow-1"
                    v-model="repoPicker.create.selectedSlug"
                    @change="onRepoDropdownSelect('create')"
                  >
                    <option value="">+ Attach a Bitbucket repository ({{ bitbucketRepoOptions.length }} available)...</option>
                    <option
                      v-for="opt in bitbucketRepoOptions"
                      :key="'c-repo-' + opt.repo_slug"
                      :value="opt.repo_slug"
                    >
                      {{ opt.repo_slug }}  —  {{ (opt.suggested_role || opt.role || 'repo').toUpperCase() }}{{ isRepoAttached('create', opt.repo_slug) ? ' (Attached)' : '' }}
                    </option>
                    <option value="__custom__">+ Enter custom repository slug...</option>
                  </select>
                </div>

                <div v-if="repoPicker.create.selectedSlug === '__custom__'" class="d-flex align-items-center gap-1.5 mb-2">
                  <input
                    type="text"
                    class="form-control form-control-sm text-xs font-monospace bg-white flex-grow-1"
                    v-model="repoPicker.create.customSlug"
                    placeholder="Enter Bitbucket repo slug (e.g. xsurflog_be)"
                    @keydown.enter.prevent="addRepositoryToTarget('create')"
                  />
                  <button
                    type="button"
                    class="btn btn-cyan-primary btn-xs px-2.5 py-1 fw-bold flex-shrink-0"
                    @click="addRepositoryToTarget('create')"
                    :disabled="!repoPicker.create.customSlug"
                  >
                    <i class="fa-solid fa-plus me-1"></i>Attach
                  </button>
                </div>

                <div v-if="platformForm.repositories && platformForm.repositories.length > 0" class="d-flex flex-column gap-1.5">
                  <div
                    v-for="(repo, rIdx) in platformForm.repositories"
                    :key="'c-attached-' + rIdx + '-' + repo.repo_slug"
                    class="d-flex align-items-center justify-content-between px-2.5 py-1.5 rounded-2 bg-white border text-xs repo-attached-row"
                  >
                    <div class="d-flex align-items-center gap-2 text-truncate min-w-0">
                      <select
                        class="form-select form-select-sm repo-role-badge-select fw-bold text-uppercase font-monospace"
                        v-model="repo.role"
                        title="Change repository role"
                      >
                        <option v-for="rOpt in repoRoleOptions" :key="'c-r-' + rOpt.value" :value="rOpt.value">
                          {{ rOpt.shortLabel || rOpt.value.toUpperCase() }}
                        </option>
                      </select>
                      <span class="font-monospace fw-bold text-dark-blue text-truncate">
                        {{ repo.workspace || 'VertexSecurity' }}/{{ repo.repo_slug }}
                      </span>
                    </div>
                    <div class="d-flex align-items-center gap-2 flex-shrink-0 ms-2">
                      <span class="badge bg-light text-secondary border font-monospace text-xxs">
                        <i class="fa-solid fa-code-branch me-1 text-info"></i>{{ repo.branch || 'dev' }}
                      </span>
                      <button type="button" class="btn btn-link btn-xs text-muted hover-danger p-0" @click="removeRepositoryFromTarget('create', rIdx)" title="Remove repository">
                        <i class="fa-solid fa-xmark"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div v-else class="text-xxs text-muted-custom px-2.5 py-2 bg-white rounded-2 border text-center">
                  No repositories attached yet. Select a repository above to attach it.
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer bg-light border-top py-2.5 px-4">
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeModalSafe('platformFormModal')">Cancel</button>
            <button type="button" class="btn btn-cyan-primary btn-sm fw-bold px-3" @click="saveNewPlatform" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="fa-solid fa-check me-1"></i>
              <span>Create Platform</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- EDIT PLATFORM MODAL -->
    <div class="modal fade" id="editPlatformModal" tabindex="-1" aria-hidden="true" ref="editPlatformModal">
      <div class="modal-dialog modal-dialog-scrollable custom-modal-scrollable" style="max-width: 620px; margin: 1.75rem auto;">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-white border-bottom sticky-top py-3 px-4">
            <div class="d-flex align-items-center gap-2">
              <h5 class="modal-title fw-bold text-dark-blue mb-0">
                <i class="fa-solid fa-sliders text-info me-2"></i> Edit Platform Settings
              </h5>
              <span v-if="editPlatformForm.key" class="badge bg-light text-secondary border font-monospace text-xxs">{{ editPlatformForm.key }}</span>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="closeModalSafe('editPlatformModal')"></button>
          </div>
          <div class="modal-body custom-modal-body p-4">
            <form @submit.prevent="saveEditPlatform">
              <!-- Basic Identity Row -->
              <div class="row g-2.5 mb-2.5">
                <div class="col-md-7">
                  <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Platform Name *</label>
                  <input type="text" class="form-control form-control-sm" v-model="editPlatformForm.name" required />
                </div>
                <div class="col-md-5">
                  <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Platform Key *</label>
                  <input type="text" class="form-control form-control-sm font-monospace" v-model="editPlatformForm.key" required />
                </div>
              </div>

              <!-- Description -->
              <div class="mb-2.5">
                <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Description</label>
                <textarea class="form-control form-control-sm" rows="2" v-model="editPlatformForm.description" placeholder="Brief summary of this platform..."></textarea>
              </div>

              <!-- Compact Inline Theme Accent Color Bar -->
              <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-2 mb-3 bg-light rounded-3 border">
                <span class="fw-bold text-secondary text-xxs text-uppercase">
                  <i class="fa-solid fa-palette text-info me-1.5"></i> Theme Accent Color
                </span>
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <div class="d-flex align-items-center gap-1.5">
                    <input type="color" class="form-control form-control-color border-0 p-0 bg-transparent" v-model="editPlatformForm.primary_color" style="width: 24px; height: 24px; cursor: pointer;" />
                    <input type="text" class="form-control form-control-sm text-xxs font-monospace bg-white py-0 px-1.5" v-model="editPlatformForm.primary_color" style="width: 78px; height: 24px;" />
                  </div>
                  <div class="d-flex align-items-center gap-1 ms-1">
                    <button
                      v-for="preset in colorPresets"
                      :key="'edit-' + preset"
                      type="button"
                      class="color-preset-btn-sm rounded-circle border-0"
                      :class="{ active: (editPlatformForm.primary_color || '').toLowerCase() === preset.toLowerCase() }"
                      :style="{ backgroundColor: preset }"
                      @click="editPlatformForm.primary_color = preset"
                    ></button>
                  </div>
                </div>
              </div>

              <!-- Card 1: Jira Board Routing -->
              <div class="p-3 bg-light rounded-3 border mb-3">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <span class="fw-bold text-dark-blue text-xs">
                    <i class="fa-solid fa-diagram-project text-info me-1.5"></i> Jira Board Routing
                  </span>
                  <a
                    :href="`https://${editPlatformForm.jira_domain || 'vertex.atlassian.net'}/jira/core/projects/${(editPlatformForm.jira_project_key || 'DEV').toUpperCase()}/board`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-decoration-none text-xxs fw-bold text-primary d-inline-flex align-items-center gap-1"
                  >
                    <span>Open {{ (editPlatformForm.jira_project_key || 'DEV').toUpperCase() }} Board</span>
                    <i class="fa-solid fa-arrow-up-right-from-square" style="font-size: 9px;"></i>
                  </a>
                </div>
                <div class="row g-2 mb-2">
                  <div class="col-sm-4">
                    <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Board / Project Key *</label>
                    <input
                      type="text"
                      class="form-control form-control-sm font-monospace text-uppercase fw-bold bg-white"
                      v-model="editPlatformForm.jira_project_key"
                      placeholder="DEV"
                    />
                  </div>
                  <div class="col-sm-8">
                    <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Jira Cloud Domain</label>
                    <input
                      type="text"
                      class="form-control form-control-sm font-monospace bg-white"
                      v-model="editPlatformForm.jira_domain"
                      placeholder="vertex.atlassian.net"
                    />
                  </div>
                </div>
                <div class="form-check form-switch pt-2 border-top d-flex align-items-center justify-content-between ms-0 mb-0">
                  <label class="form-check-label text-xs text-dark-blue fw-semibold" for="editPlatAutoSync">
                    Auto-push new bug submissions directly to <strong>{{ (editPlatformForm.jira_project_key || 'DEV').toUpperCase() }}</strong> board
                  </label>
                  <input class="form-check-input mt-0 ms-2" type="checkbox" role="switch" id="editPlatAutoSync" v-model="editPlatformForm.jira_auto_sync" style="width: 2.4em; height: 1.25em; cursor: pointer;" />
                </div>
              </div>

              <!-- Card 2: Bitbucket Repositories -->
              <div class="p-3 bg-light rounded-3 border">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <span class="fw-bold text-dark-blue text-xs">
                    <i class="fa-solid fa-code-branch text-info me-1.5"></i> Bitbucket Repositories
                  </span>
                  <div class="d-flex align-items-center gap-1.5">
                    <span class="badge bg-white text-secondary border font-monospace text-xxs">
                      {{ (editPlatformForm.repositories || []).length }} Attached &bull; {{ bitbucketRepoOptions.length }} Available
                    </span>
                    <button
                      type="button"
                      class="btn btn-white bg-white border btn-xs px-1.5 py-0.5 text-info"
                      @click="fetchBitbucketRepositories()"
                      title="Refresh repositories from Bitbucket Cloud"
                    >
                      <i class="fa-solid fa-arrows-rotate" :class="{ 'fa-spin': isLoadingBitbucketRepos }" style="font-size: 10px;"></i>
                    </button>
                  </div>
                </div>

                <!-- Streamlined One-Step Repo Selector -->
                <div class="d-flex align-items-center gap-1.5 mb-2">
                  <select
                    class="form-select form-select-sm text-xs font-monospace bg-white flex-grow-1"
                    v-model="repoPicker.edit.selectedSlug"
                    @change="onRepoDropdownSelect('edit')"
                  >
                    <option value="">+ Attach a Bitbucket repository ({{ bitbucketRepoOptions.length }} available)...</option>
                    <option
                      v-for="opt in bitbucketRepoOptions"
                      :key="'e-repo-' + opt.repo_slug"
                      :value="opt.repo_slug"
                    >
                      {{ opt.repo_slug }}  —  {{ (opt.suggested_role || opt.role || 'repo').toUpperCase() }}{{ isRepoAttached('edit', opt.repo_slug) ? ' (Attached)' : '' }}
                    </option>
                    <option value="__custom__">+ Enter custom repository slug...</option>
                  </select>
                </div>

                <div v-if="repoPicker.edit.selectedSlug === '__custom__'" class="d-flex align-items-center gap-1.5 mb-2">
                  <input
                    type="text"
                    class="form-control form-control-sm text-xs font-monospace bg-white flex-grow-1"
                    v-model="repoPicker.edit.customSlug"
                    placeholder="Enter Bitbucket repo slug (e.g. xsurflog_be)"
                    @keydown.enter.prevent="addRepositoryToTarget('edit')"
                  />
                  <button
                    type="button"
                    class="btn btn-cyan-primary btn-xs px-2.5 py-1 fw-bold flex-shrink-0"
                    @click="addRepositoryToTarget('edit')"
                    :disabled="!repoPicker.edit.customSlug"
                  >
                    <i class="fa-solid fa-plus me-1"></i>Attach
                  </button>
                </div>

                <div v-if="editPlatformForm.repositories && editPlatformForm.repositories.length > 0" class="d-flex flex-column gap-1.5">
                  <div
                    v-for="(repo, rIdx) in editPlatformForm.repositories"
                    :key="'e-attached-' + rIdx + '-' + repo.repo_slug"
                    class="d-flex align-items-center justify-content-between px-2.5 py-1.5 rounded-2 bg-white border text-xs repo-attached-row"
                  >
                    <div class="d-flex align-items-center gap-2 text-truncate min-w-0">
                      <select
                        class="form-select form-select-sm repo-role-badge-select fw-bold text-uppercase font-monospace"
                        v-model="repo.role"
                        title="Change repository role"
                      >
                        <option v-for="rOpt in repoRoleOptions" :key="'e-r-' + rOpt.value" :value="rOpt.value">
                          {{ rOpt.shortLabel || rOpt.value.toUpperCase() }}
                        </option>
                      </select>
                      <span class="font-monospace fw-bold text-dark-blue text-truncate">
                        {{ repo.workspace || editPlatformForm.bitbucket_workspace || 'VertexSecurity' }}/{{ repo.repo_slug }}
                      </span>
                    </div>
                    <div class="d-flex align-items-center gap-2 flex-shrink-0 ms-2">
                      <span class="badge bg-light text-secondary border font-monospace text-xxs">
                        <i class="fa-solid fa-code-branch me-1 text-info"></i>{{ repo.branch || 'dev' }}
                      </span>
                      <button type="button" class="btn btn-link btn-xs text-muted hover-danger p-0" @click="removeRepositoryFromTarget('edit', rIdx)" title="Remove repository">
                        <i class="fa-solid fa-xmark"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div v-else class="text-xxs text-muted-custom px-2.5 py-2 bg-white rounded-2 border text-center">
                  No repositories attached yet. Select a repository above to attach it.
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer bg-light border-top py-2.5 px-4">
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeModalSafe('editPlatformModal')">Cancel</button>
            <button type="button" class="btn btn-cyan-primary btn-sm fw-bold px-3" @click="saveEditPlatform" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="fa-solid fa-check me-1"></i>
              <span>Save Changes</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- DELETE PLATFORM CONFIRMATION MODAL -->
    <div class="modal fade" id="deletePlatformModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content border-0 shadow text-center p-3">
          <div class="modal-body">
            <div class="delete-icon-box mx-auto mb-3">
              <i class="fa-solid fa-triangle-exclamation text-danger fa-2x"></i>
            </div>
            <h5 class="fw-bold text-dark-blue mb-2">Delete Platform?</h5>
            <p class="text-muted-custom text-xs mb-4">Are you sure you want to delete platform <strong>{{ platformToDelete?.name }}</strong>? This action cannot be undone.</p>
            <div class="d-flex justify-content-center gap-2">
              <button type="button" class="btn btn-outline-secondary btn-sm px-3" @click="closeModalSafe('deletePlatformModal')">Cancel</button>
              <button type="button" class="btn btn-danger btn-sm fw-bold px-3" @click="deletePlatform" :disabled="isSubmitting">
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ADMIN TRIAGE & AI AUTO-FIX PIPELINE MODAL -->
    <div class="modal fade" id="triageModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-scrollable custom-modal-scrollable">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-white border-bottom sticky-top">
            <h5 class="modal-title fw-bold text-dark-blue d-flex align-items-center gap-2">
              <i class="fa-solid fa-wand-magic-sparkles text-cyan-primary"></i>
              <span>Bug Triage, AI Auto-Fix &amp; SDLC Pipeline</span>
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="closeModalSafe('triageModal')"></button>
          </div>
          <div class="modal-body custom-modal-body p-4" v-if="activeTriageReport">
            <!-- Core Loop Visual Banner: Report Bug -> Jira -> Automate Fixing -> Update Jira -->
            <div class="p-3 mb-4 rounded-3 border" style="background: linear-gradient(135deg, #f0f9ff 0%, #f8fafc 100%); border-color: #bae6fd !important;">
              <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <div class="d-flex align-items-center gap-2">
                  <span class="badge bg-cyan-subtle text-cyan-primary fw-bold text-xxs px-2 py-1">CLOSED-LOOP PIPELINE</span>
                  <span class="fw-bold text-xs text-dark-blue">Report Bug &rarr; Jira &rarr; Automate Fixing &rarr; Update Jira</span>
                </div>
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-xs px-3 py-1 fw-bold d-inline-flex align-items-center gap-1 opacity-50"
                  disabled
                  title="AI Auto-Fix is temporarily parked"
                >
                  <i class="fa-solid fa-terminal"></i>
                  <span>Queue &amp; Copy AI Fix Command (Parked)</span>
                </button>
              </div>

              <!-- 4-Step Core Loop Progress Pills -->
              <div class="row g-2 text-center mt-1">
                <div class="col-3">
                  <div class="p-2 rounded-2 border bg-white border-success">
                    <div class="text-xxs fw-bold text-success"><i class="fa-solid fa-check-circle me-1"></i>1. Reported</div>
                    <div class="text-xxs text-muted-custom text-truncate">#{{ activeTriageReport.id }} Logged</div>
                  </div>
                </div>
                <div class="col-3">
                  <div class="p-2 rounded-2 border bg-white" :class="activeTriageReport.jira_issue_key ? 'border-success' : 'border-secondary-subtle'">
                    <div class="text-xxs fw-bold" :class="activeTriageReport.jira_issue_key ? 'text-success' : 'text-secondary'">
                      <i class="fa-solid me-1" :class="activeTriageReport.jira_issue_key ? 'fa-check-circle' : 'fa-ticket'"></i>2. Jira Ticket
                    </div>
                    <div class="text-xxs text-muted-custom text-truncate">{{ activeTriageReport.jira_issue_key || 'Pending Sync' }}</div>
                  </div>
                </div>
                <div class="col-3">
                  <div class="p-2 rounded-2 border bg-white opacity-50 border-secondary-subtle">
                    <div class="text-xxs fw-bold text-secondary">
                      <i class="fa-solid fa-wand-magic-sparkles me-1"></i>3. AI Auto-Fix
                    </div>
                    <div class="text-xxs text-muted-custom text-truncate">Parked</div>
                  </div>
                </div>
                <div class="col-3">
                  <div class="p-2 rounded-2 border bg-white" :class="activeTriageReport.ai_updated_at && activeTriageReport.jira_issue_key ? 'border-success' : 'border-secondary-subtle'">
                    <div class="text-xxs fw-bold" :class="activeTriageReport.ai_updated_at && activeTriageReport.jira_issue_key ? 'text-success' : 'text-secondary'">
                      <i class="fa-solid fa-rotate me-1"></i>4. Jira Updated
                    </div>
                    <div class="text-xxs text-muted-custom text-truncate">{{ activeTriageReport.ai_updated_at ? formatDate(activeTriageReport.ai_updated_at) : 'Awaiting Fix' }}</div>
                  </div>
                </div>
              </div>

              <!-- Full SDLC Workflow Stage Selector Bar -->
              <div class="mt-3 pt-2 border-top">
                <div class="d-flex align-items-center justify-content-between mb-1">
                  <span class="text-xxs fw-bold text-secondary text-uppercase">Full Release &amp; SDLC Stage Progression</span>
                  <span class="badge rounded-pill text-xxs" :class="getWorkflowStageBadgeClass(activeTriageReport.workflow_stage)">
                    {{ getWorkflowStageLabel(activeTriageReport.workflow_stage) }}
                  </span>
                </div>
                <div class="d-flex flex-wrap gap-1">
                  <button
                    v-for="st in workflowStages"
                    :key="st.value"
                    type="button"
                    class="btn btn-xs py-1 px-2 text-xxs fw-semibold rounded-pill"
                    :class="(activeTriageReport.workflow_stage || 'reported') === st.value ? 'btn-cyan-primary text-white shadow-sm' : 'btn-outline-secondary bg-white'"
                    @click="activeTriageReport.workflow_stage = st.value"
                  >
                    <i :class="[st.icon, 'me-1']"></i>{{ st.label }}
                  </button>
                </div>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-8">
                <h5 class="fw-bold text-dark-blue mb-2">{{ activeTriageReport.title }}</h5>
                <p class="text-muted-custom text-xs mb-0" style="white-space: pre-line;">{{ activeTriageReport.description }}</p>
              </div>
              <div class="col-md-4 bg-light p-3 rounded-3 border">
                <div class="mb-2">
                  <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Report Status</label>
                  <select class="form-select form-select-sm text-xs" v-model="activeTriageReport.status">
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div>
                  <label class="form-label fw-bold text-secondary text-xxs text-uppercase mb-1">Severity</label>
                  <select class="form-select form-select-sm text-xs" v-model="activeTriageReport.severity">
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- AI Triage Verdict, Local Dev Fix Metadata & Jira Update Form -->
            <div class="p-3 mb-3 rounded-3 border bg-white shadow-xs opacity-75">
              <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                <h6 class="fw-bold text-dark-blue text-xs text-uppercase mb-0">
                  <i class="fa-solid fa-robot text-secondary me-1"></i> AI Root-Cause Analysis &amp; Local Dev Fix Details
                  <span class="badge bg-secondary-subtle text-secondary border ms-1">Parked</span>
                </h6>
                <span class="text-xxs text-muted-custom">AI Auto-Fix is temporarily parked</span>
              </div>

              <!-- Attached Multi-Repo Context Banner (Sequential on unified dev branch) -->
              <div
                v-if="widgetForm.repositories && widgetForm.repositories.length > 0"
                class="p-2 mb-2 rounded-2 bg-light border d-flex align-items-center justify-content-between flex-wrap gap-2"
              >
                <div class="d-flex align-items-center gap-1 flex-wrap">
                  <span class="text-xxs fw-bold text-secondary text-uppercase me-1">
                    <i class="fa-solid fa-code-branch text-cyan-primary me-1"></i>Project Repos (<code>dev</code>):
                  </span>
                  <span
                    v-for="(repo, rIdx) in widgetForm.repositories"
                    :key="'triage-repo-' + rIdx"
                    class="badge bg-white text-dark-blue border font-monospace text-xxs"
                  >
                    <strong class="text-cyan-primary text-uppercase me-1">{{ repo.role || 'repo' }}:</strong>{{ repo.workspace || widgetForm.bitbucket_workspace || 'VertexSecurity' }}/{{ repo.repo_slug }}
                  </span>
                </div>
                <span class="badge bg-success-subtle text-success border border-success-subtle text-xxs">
                  Unified <code>dev</code> branch &bull; Sequential Queue
                </span>
              </div>

              <div class="row g-2 mb-2">
                <div class="col-md-3">
                  <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">AI Triage Verdict</label>
                  <select class="form-select form-select-sm text-xs" v-model="activeTriageReport.ai_verdict" disabled>
                    <option value="">-- Pending Triage --</option>
                    <option value="legit_bug">Legit Bug (Confirmed)</option>
                    <option value="needs_info">Needs More Info</option>
                    <option value="false_positive">Not a Bug / By Design</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">AI Pipeline Status</label>
                  <select class="form-select form-select-sm text-xs" v-model="activeTriageReport.ai_status" disabled>
                    <option value="idle">Idle</option>
                    <option value="queued">Queued for AI</option>
                    <option value="triaging">AI Triaging</option>
                    <option value="fixing">Applying Fix</option>
                    <option value="fixed_local">Fixed in Local Dev</option>
                    <option value="skipped">Skipped</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Git Branch (Unified)</label>
                  <input type="text" class="form-control form-control-sm text-xs font-monospace" v-model="activeTriageReport.ai_branch" placeholder="dev" disabled />
                </div>
                <div class="col-md-3">
                  <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Commit Hash</label>
                  <input type="text" class="form-control form-control-sm text-xs font-monospace" v-model="activeTriageReport.ai_commit_hash" placeholder="e.g. a1b2c3d" disabled />
                </div>
              </div>

              <div class="mb-2">
                <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">Modified Files (Comma or newline separated)</label>
                <input type="text" class="form-control form-control-sm text-xs font-monospace" v-model="activeTriageReport.ai_files_changed" placeholder="e.g. spark_be_dev/controllers/platformBugReportController.go, spark_fe/src/views/..." disabled />
              </div>

              <div class="mb-2">
                <label class="form-label fw-semibold text-secondary text-xxs text-uppercase mb-1">AI Root-Cause Analysis &amp; Resolution Summary</label>
                <textarea class="form-control text-xs" rows="3" v-model="activeTriageReport.ai_analysis" placeholder="AI Auto-Fix is temporarily parked..." disabled></textarea>
              </div>

              <div class="d-flex justify-content-end pt-1">
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-xs px-3 py-1 fw-bold d-inline-flex align-items-center gap-1 opacity-50"
                  disabled
                  title="AI Auto-Fix is temporarily parked"
                >
                  <i class="fa-solid fa-cloud-arrow-up"></i>
                  <span>AI Auto-Fix Parked</span>
                </button>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label fw-bold text-secondary text-xs text-uppercase">Admin Triage Notes</label>
              <textarea class="form-control text-xs" rows="2" v-model="activeTriageReport.admin_notes" placeholder="Enter resolution notes, assigned engineer, or UAT deployment notes..."></textarea>
            </div>

            <!-- Jira Integration Status inside Triage Modal -->
            <div class="p-3 bg-light rounded-3 border d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div class="d-flex align-items-center gap-2">
                <i class="fa-solid fa-ticket text-primary fs-5"></i>
                <div>
                  <div class="fw-bold text-xs text-dark-blue">
                    Jira Cloud Ticket ({{ widgetForm.jira_project_key || 'DEV' }})
                  </div>
                  <div class="text-xxs text-muted-custom">
                    <span v-if="activeTriageReport.jira_issue_key">
                      Synced as <strong>{{ activeTriageReport.jira_issue_key }}</strong>
                      <span v-if="activeTriageReport.jira_pushed_by"> &bull; via {{ activeTriageReport.jira_pushed_by }}</span>
                    </span>
                    <span v-else>
                      Will push using <strong>{{ userJiraCred.has_personal_token ? ('your token (' + userJiraCred.jira_email + ')') : 'General Platform Token' }}</strong>
                    </span>
                  </div>
                </div>
              </div>
              <div class="d-flex align-items-center gap-2">
                <a
                  v-if="activeTriageReport.jira_issue_key"
                  :href="activeTriageReport.jira_issue_url || ('https://' + (widgetForm.jira_domain || 'vertex.atlassian.net') + '/browse/' + activeTriageReport.jira_issue_key)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn btn-primary btn-xs px-3 py-1 fw-bold d-inline-flex align-items-center gap-1 text-white text-decoration-none"
                >
                  <i class="fa-solid fa-ticket"></i>
                  <span>Open {{ activeTriageReport.jira_issue_key }}</span>
                  <i class="fa-solid fa-arrow-up-right-from-square ms-1" style="font-size: 9px;"></i>
                </a>
                <button
                  type="button"
                  class="btn btn-xs px-3 py-1 fw-bold d-inline-flex align-items-center gap-1"
                  :class="activeTriageReport.jira_issue_key ? 'btn-outline-primary' : 'btn-primary'"
                  @click="pushReportToJira(activeTriageReport, !!activeTriageReport.jira_issue_key)"
                  :disabled="pushingJiraId === activeTriageReport.id || isSubmitting"
                >
                  <span v-if="pushingJiraId === activeTriageReport.id" class="spinner-border spinner-border-sm" style="width: 12px; height: 12px;"></span>
                  <i v-else class="fa-solid fa-ticket"></i>
                  <span>{{ pushingJiraId === activeTriageReport.id ? 'Pushing to Jira...' : (activeTriageReport.jira_issue_key ? 'Re-Push New Ticket' : 'Push to Jira') }}</span>
                </button>
                <button
                  v-if="activeTriageReport.jira_issue_key"
                  type="button"
                  class="btn btn-outline-danger btn-xs px-2 py-1 fw-semibold d-inline-flex align-items-center gap-1"
                  @click="unlinkJiraFromReport(activeTriageReport)"
                  :disabled="pushingJiraId === activeTriageReport.id || isSubmitting"
                  title="Unlink this Jira ticket and reset to Push to Jira"
                >
                  <i class="fa-solid fa-link-slash"></i>
                  <span>Unlink</span>
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer bg-light border-top">
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeModalSafe('triageModal')">Cancel</button>
            <button type="button" class="btn btn-cyan-primary btn-sm fw-bold px-3" @click="saveTriageNotes" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
              <span>Save Triage Notes</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- SCREENSHOT LIGHTBOX MODAL -->
    <div class="modal fade" id="screenshotLightboxModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content border-0 bg-dark text-white p-2">
          <div class="modal-header border-bottom border-secondary py-2">
            <h6 class="modal-title fw-bold text-white"><i class="fa-solid fa-image me-2 text-info"></i> Captured Screenshot &amp; Annotations</h6>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close" @click="closeModalSafe('screenshotLightboxModal')"></button>
          </div>
          <div class="modal-body text-center p-3 overflow-auto" style="max-height: 80vh;">
            <img v-if="activeLightboxUrl" :src="activeLightboxUrl" class="img-fluid rounded border shadow" alt="Full Resolution Screenshot" />
          </div>
        </div>
      </div>
    </div>

    <!-- VIDEO PLAYER MODAL -->
    <div class="modal fade" id="videoPlayerModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content border-0 bg-dark text-white p-2">
          <div class="modal-header border-bottom border-secondary py-2">
            <h6 class="modal-title fw-bold text-white"><i class="fa-solid fa-video me-2 text-danger"></i> Screen Recording Capture</h6>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close" @click="closeModalSafe('videoPlayerModal')"></button>
          </div>
          <div class="modal-body text-center p-3">
            <video v-if="activeVideoUrl" controls autoplay class="w-100 rounded border" style="max-height: 70vh;" :src="activeVideoUrl"></video>
          </div>
        </div>
      </div>
    </div>

    <!-- LIVE SANDBOX SUBMIT MODAL / DRAWER -->
    <div class="modal fade" id="sandboxSubmitModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-scrollable custom-modal-scrollable">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-white border-bottom sticky-top">
            <h5 class="modal-title fw-bold text-dark-blue">
              <i class="fa-solid fa-bug text-cyan-primary me-2"></i> Report a Bug / Test Widget Drawer
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="closeModalSafe('sandboxSubmitModal')"></button>
          </div>
          <div class="modal-body custom-modal-body p-4">
            <form @submit.prevent="submitSandboxBugReport">
              <div class="row g-3 mb-3">
                <div class="col-md-8">
                  <label class="form-label fw-bold text-secondary text-xs text-uppercase">Issue Title *</label>
                  <input type="text" class="form-control text-xs" v-model="sandboxForm.title" placeholder="e.g. Checkout button unresponsive on mobile" required />
                </div>
                <div class="col-md-4">
                  <label class="form-label fw-bold text-secondary text-xs text-uppercase">Severity</label>
                  <select class="form-select form-select-sm text-xs" v-model="sandboxForm.severity">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>

              <div class="row g-3 mb-3">
                <div class="col-md-4">
                  <label class="form-label fw-bold text-secondary text-xs text-uppercase">Issue Type</label>
                  <select class="form-select form-select-sm text-xs" v-model="sandboxForm.issue_type">
                    <option value="bug">Bug Report</option>
                    <option value="feedback">Feedback</option>
                    <option value="ui">UI / UX</option>
                  </select>
                </div>
                <div class="col-md-4">
                  <label class="form-label fw-bold text-secondary text-xs text-uppercase">Reporter Name</label>
                  <input type="text" class="form-control text-xs" v-model="sandboxForm.reporter_name" placeholder="John Doe" />
                </div>
                <div class="col-md-4">
                  <label class="form-label fw-bold text-secondary text-xs text-uppercase">Reporter Email</label>
                  <input type="email" class="form-control text-xs" v-model="sandboxForm.reporter_email" placeholder="john@example.com" />
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase">Description *</label>
                <textarea class="form-control text-xs" rows="3" v-model="sandboxForm.description" placeholder="Describe what happened and how to reproduce..." required></textarea>
              </div>

              <!-- Interactive Canvas Annotation Tool -->
              <div class="mb-3">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <span>Interactive Canvas Screenshot Capture</span>
                  <div class="d-flex align-items-center gap-1 flex-wrap">
                    <button
                      type="button"
                      class="btn btn-xs"
                      :class="annoTool === 'pen' ? 'btn-cyan-primary' : 'btn-outline-secondary'"
                      @click="selectSandboxAnnoTool('pen')"
                      title="Freehand Pen"
                    >
                      <i class="fa-solid fa-pen me-1"></i>Pen
                    </button>
                    <button
                      type="button"
                      class="btn btn-xs"
                      :class="annoTool === 'rect' ? 'btn-cyan-primary' : 'btn-outline-secondary'"
                      @click="selectSandboxAnnoTool('rect')"
                      title="Box (with optional label)"
                    >
                      <i class="fa-regular fa-square me-1"></i>Box
                    </button>
                    <button
                      type="button"
                      class="btn btn-xs"
                      :class="annoTool === 'arrow' ? 'btn-cyan-primary' : 'btn-outline-secondary'"
                      @click="selectSandboxAnnoTool('arrow')"
                      title="Arrow (with optional callout label)"
                    >
                      <i class="fa-solid fa-arrow-trend-up me-1"></i>Arrow
                    </button>
                    <button
                      type="button"
                      class="btn btn-xs"
                      :class="annoTool === 'text' ? 'btn-cyan-primary' : 'btn-outline-secondary'"
                      @click="selectSandboxAnnoTool('text')"
                      title="Add Text Callout"
                    >
                      <i class="fa-solid fa-font me-1"></i>Text
                    </button>
                    <button
                      type="button"
                      class="btn btn-xs"
                      :class="annoTool === 'blur' ? 'btn-cyan-primary' : 'btn-outline-secondary'"
                      @click="selectSandboxAnnoTool('blur')"
                      title="Redact Sensitive Area"
                    >
                      <i class="fa-solid fa-eye-slash me-1"></i>Redact
                    </button>
                    <button
                      type="button"
                      class="btn btn-xs btn-outline-secondary px-2 py-1"
                      :disabled="sandboxHistory.length <= 1"
                      @click="undoSandboxCanvas"
                    >
                      <i class="fa-solid fa-rotate-left me-1"></i>Undo
                    </button>
                    <button type="button" class="btn btn-xs btn-outline-secondary px-2 py-1" @click="clearCanvas">Clear</button>
                    <button type="button" class="btn btn-xs btn-cyan-outline px-2 py-1" @click="captureSandboxViewport">Capture Screen</button>
                  </div>
                </label>
                <div
                  ref="sandboxCanvasContainer"
                  class="border rounded-3 bg-light p-2 text-center overflow-hidden position-relative"
                  style="min-height: 180px;"
                >
                  <canvas
                    ref="annotationCanvas"
                    width="700"
                    height="220"
                    class="bg-white border rounded shadow-xs w-100"
                    :style="{ touchAction: 'none', cursor: annoTool === 'text' ? 'text' : 'crosshair' }"
                    @mousedown="startSandboxDrawing"
                    @mousemove="drawSandbox"
                    @mouseup="stopSandboxDrawing"
                    @mouseleave="stopSandboxDrawing"
                    @touchstart.prevent="startSandboxTouch"
                    @touchmove.prevent="drawSandboxTouch"
                    @touchend.prevent="stopSandboxDrawing"
                  ></canvas>

                  <!-- Floating On-Canvas Text Callout Input -->
                  <div
                    v-if="sandboxTextOverlay.visible"
                    class="sandbox-canvas-text-overlay d-flex align-items-center gap-1"
                    :style="{
                      left: sandboxTextOverlay.cssLeft + 'px',
                      top: sandboxTextOverlay.cssTop + 'px',
                      borderColor: annoColor,
                    }"
                    @mousedown.stop
                    @click.stop
                  >
                    <input
                      ref="sandboxCanvasTextInput"
                      v-model="sandboxTextOverlay.text"
                      type="text"
                      class="sandbox-canvas-text-input"
                      :placeholder="
                        sandboxTextOverlay.isShapeLabel
                          ? 'Add label to shape (Enter to apply, Esc to skip)...'
                          : 'Type text annotation (Enter to place)...'
                      "
                      @keydown.enter.prevent="commitSandboxCanvasText"
                      @keydown.esc.prevent="cancelSandboxCanvasText"
                    />
                    <button
                      type="button"
                      class="btn btn-xs btn-cyan-primary py-0 px-2"
                      @click="commitSandboxCanvasText"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      class="btn btn-xs btn-outline-light py-0 px-2"
                      @click="cancelSandboxCanvasText"
                    >
                      &times;
                    </button>
                  </div>

                  <span class="text-muted-custom text-xxs d-block mt-1">
                    Use mouse/touch to draw shapes, arrows, or click to add text labels directly on the screenshot preview!
                  </span>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer bg-light border-top">
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeModalSafe('sandboxSubmitModal')">Cancel</button>
            <button type="button" class="btn btn-cyan-primary btn-sm fw-bold px-3" @click="submitSandboxBugReport" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="fa-solid fa-paper-plane me-1"></i>
              <span>Submit Bug Report</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- =======================================================================
         MODAL: MY PERSONAL ATLASSIAN CREDENTIALS (Manual Push Identity)
         ======================================================================= -->
    <div class="modal fade" id="userJiraCredModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-scrollable custom-modal-scrollable" style="max-width: 580px; margin: 1.75rem auto;">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-white border-bottom sticky-top">
            <h5 class="modal-title fw-bold text-dark-blue">
              <i class="fa-solid fa-key text-info me-2"></i> My Atlassian Token
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="closeModalSafe('userJiraCredModal')"></button>
          </div>
          <div class="modal-body custom-modal-body p-4">
            <!-- Active status banner if configured -->
            <div v-if="userJiraCred.has_personal_token" class="d-flex align-items-center justify-content-between p-2.5 rounded-3 mb-3 bg-light border">
              <div class="d-flex align-items-center gap-2 px-1">
                <i class="fa-solid fa-circle-check text-success"></i>
                <div>
                  <div class="fw-bold text-dark-blue text-xs">
                    Connected as {{ userJiraCred.jira_display_name || userJiraCred.jira_email }}
                  </div>
                  <div class="text-muted text-xs">
                    {{ userJiraCred.jira_email }} &bull; Token: <code>{{ userJiraCred.masked_token || '••••••••' }}</code>
                  </div>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label fw-bold text-secondary text-xs text-uppercase">Atlassian / Jira Cloud Domain</label>
              <input
                type="text"
                class="form-control font-monospace"
                v-model="userJiraCred.jira_domain"
                placeholder="vertex.atlassian.net"
                :disabled="userJiraCred.has_personal_token"
              />
            </div>

            <div class="mb-3">
              <label class="form-label fw-bold text-secondary text-xs text-uppercase">Your Atlassian Account Email *</label>
              <input
                type="email"
                class="form-control"
                v-model="userJiraCred.jira_email"
                placeholder="you@vtxsecurity.com"
                :disabled="userJiraCred.has_personal_token"
              />
            </div>

            <div v-if="!userJiraCred.has_personal_token" class="mb-3">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase mb-0">
                  Your Personal Atlassian API Token *
                </label>
                <a
                  href="https://id.atlassian.com/manage-profile/security/api-tokens"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-xs text-decoration-none fw-semibold text-primary"
                >
                  Create Token on Atlassian <i class="fa-solid fa-arrow-up-right-from-square ms-1"></i>
                </a>
              </div>
              <input
                type="password"
                class="form-control font-monospace"
                v-model="userJiraCred.jira_api_token"
                placeholder="ATATT3xFfGF0..."
              />
              <small class="text-muted d-block mt-1 text-xs">
                Used when you manually click <strong>Push to Jira</strong> so Jira records you as the ticket Creator/Reporter.
              </small>
            </div>

            <div class="d-flex align-items-center justify-content-between pt-2 border-top text-xs text-muted">
              <span>You can also manage this in Platform Settings</span>
              <router-link
                to="/platform-settings/user-settings"
                class="text-decoration-none fw-semibold text-primary"
                @click="closeModalSafe('userJiraCredModal')"
              >
                Open User Settings <i class="fa-solid fa-arrow-right ms-1"></i>
              </router-link>
            </div>
          </div>
          <div class="modal-footer bg-light border-top">
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeModalSafe('userJiraCredModal')">Cancel</button>
            <button
              v-if="userJiraCred.has_personal_token"
              type="button"
              class="btn btn-outline-danger btn-sm fw-bold px-3"
              @click="deleteUserJiraCredential"
              :disabled="userJiraCred.isSaving"
            >
              <i class="fa-solid fa-trash-can me-1"></i>Remove Personal Token
            </button>
            <button
              v-else
              type="button"
              class="btn btn-cyan-primary btn-sm fw-bold px-3"
              @click="saveUserJiraCredential"
              :disabled="userJiraCred.isSaving || !userJiraCred.jira_email || !userJiraCred.jira_api_token"
            >
              <span v-if="userJiraCred.isSaving" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="fa-solid fa-check me-1"></i>
              <span>Verify &amp; Save Token</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal as BootstrapModal } from 'bootstrap';

export default {
  name: "PlatformBugReportingDashboard",
  props: {
    embeddedMode: {
      type: Boolean,
      default: false,
    },
    embeddedPlatform: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      currentView: this.embeddedMode ? "platform" : "dashboard", // 'dashboard' | 'platform'
      activeTab: "reports",     // 'reports' | 'customizer' | 'embed'

      isLoadingPlatforms: false,
      isLoadingBugReports: false,
      isSubmitting: false,
      showInlinePopoverPreview: true,

      // Managed platforms
      platforms: [],
      selectedPlatformKey: "spark",
      platformSearchQuery: "",

      // Bug Reports
      bugReports: [],
      bugSearchQuery: "",
      selectedStatusFilter: "",
      selectedTypeFilter: "",
      selectedSeverityFilter: "",

      statusFilters: [
        { label: "All", value: "" },
        { label: "Open", value: "open" },
        { label: "In Progress", value: "in_progress" },
        { label: "Resolved", value: "resolved" },
        { label: "Closed", value: "closed" },
      ],

      severityFilters: [
        { label: "All", value: "" },
        { label: "Critical", value: "critical" },
        { label: "High", value: "high" },
        { label: "Medium", value: "medium" },
        { label: "Low", value: "low" },
      ],

      colorPresets: [
        "#00a0c1",
        "#2563eb",
        "#4f46e5",
        "#059669",
        "#d97706",
        "#e11d48",
        "#0f172a",
      ],

      // Bitbucket Repositories Dropdown (populated via General Token in .env)
      isLoadingBitbucketRepos: false,
      bitbucketRepoOptions: [
        { role: "be", suggested_role: "be", workspace: "VertexSecurity", repo_slug: "spark_go", full_name: "VertexSecurity/spark_go", branch: "dev" },
        { role: "fe", suggested_role: "fe", workspace: "VertexSecurity", repo_slug: "spark_vuejs", full_name: "VertexSecurity/spark_vuejs", branch: "dev" },
        { role: "be", suggested_role: "be", workspace: "VertexSecurity", repo_slug: "xsurflog_be", full_name: "VertexSecurity/xsurflog_be", branch: "dev" },
        { role: "fe", suggested_role: "fe", workspace: "VertexSecurity", repo_slug: "xsurflog_fe", full_name: "VertexSecurity/xsurflog_fe", branch: "dev" },
        { role: "extension", suggested_role: "extension", workspace: "VertexSecurity", repo_slug: "xsurflog_extension", full_name: "VertexSecurity/xsurflog_extension", branch: "dev" },
        { role: "user-agent", suggested_role: "user-agent", workspace: "VertexSecurity", repo_slug: "xsurflog_user_agent", full_name: "VertexSecurity/xsurflog_user_agent", branch: "dev" },
      ],
      repoRoleOptions: [
        { value: "be", label: "BE (Backend)", shortLabel: "BE" },
        { value: "fe", label: "FE (Frontend)", shortLabel: "FE" },
        { value: "extension", label: "Extension", shortLabel: "EXT" },
        { value: "user-agent", label: "User Agent", shortLabel: "AGENT" },
        { value: "worker", label: "Worker / Service", shortLabel: "WORKER" },
        { value: "repo", label: "General Repo", shortLabel: "REPO" },
      ],
      repoPicker: {
        widget: { selectedSlug: "", customSlug: "", role: "be" },
        create: { selectedSlug: "", customSlug: "", role: "be" },
        edit: { selectedSlug: "", customSlug: "", role: "be" },
      },

      // Platform Form (Create)
      platformForm: {
        name: "",
        key: "",
        description: "",
        icon: "fa-solid fa-bug",
        primary_color: "#00a0c1",
        encoded_icon: "",
        icon_filename: "",
        jira_domain: "vertex.atlassian.net",
        jira_project_key: "DEV",
        jira_auto_sync: false,
        ai_auto_fix: true,
        bitbucket_workspace: "VertexSecurity",
        repositories: [],
      },

      // Platform Form (Edit)
      editPlatformForm: {
        id: null,
        name: "",
        key: "",
        description: "",
        primary_color: "#00a0c1",
        jira_domain: "vertex.atlassian.net",
        jira_project_key: "DEV",
        jira_auto_sync: false,
        ai_auto_fix: true,
        bitbucket_workspace: "VertexSecurity",
        repositories: [],
      },

      platformToDelete: null,
      pushingJiraId: null,
      queueingAiId: null,
      updatingAiId: null,

      workflowStages: [
        { value: "reported", label: "Reported", icon: "fa-solid fa-flag" },
        { value: "jira_logged", label: "Jira Logged", icon: "fa-solid fa-ticket" },
        { value: "ai_triaging", label: "AI Triaging", icon: "fa-solid fa-wand-magic-sparkles" },
        { value: "local_dev", label: "Local Dev Fix", icon: "fa-solid fa-code" },
        { value: "dev_testing", label: "Dev Testing", icon: "fa-solid fa-vial" },
        { value: "pending_uat", label: "Pending UAT", icon: "fa-solid fa-clock" },
        { value: "deployed_uat", label: "Deployed UAT", icon: "fa-solid fa-server" },
        { value: "uat_failed", label: "UAT Failed", icon: "fa-solid fa-circle-xmark" },
        { value: "ready_for_prod", label: "Ready for Prod", icon: "fa-solid fa-circle-check" },
        { value: "prod_piv", label: "Prod / PIV", icon: "fa-solid fa-rocket" },
      ],

      // Logged-in User's Personal Jira Credentials (for Manual Push)
      userJiraCred: {
        has_personal_token: false,
        jira_domain: "vertex.atlassian.net",
        jira_email: "",
        jira_api_token: "",
        jira_display_name: "",
        masked_token: "",
        isSaving: false,
      },

      // Widget Customizer Form
      widgetForm: {
        widget_position: "right-edge",
        widget_button_text: "Report a Bug",
        widget_theme: "light",
        primary_color: "#00a0c1",
        widget_enabled: true,
        widget_uuid: "",
        regenerate_uuid: false,
        jira_domain: "vertex.atlassian.net",
        jira_project_key: "DEV",
        jira_email: "",
        jira_auto_sync: false,
        ai_auto_fix: true,
        bitbucket_workspace: "VertexSecurity",
        repositories: [],
      },

      positionOptions: [
        { value: "right-edge", label: "Right Edge Tab", desc: "Vertical tab docked flush on the right edge (Recommended)" },
        { value: "left-edge", label: "Left Edge Tab", desc: "Vertical tab docked flush on the left screen edge" },
        { value: "bottom-right", label: "Bottom-Right Pill", desc: "Floating rounded pill button at bottom-right corner" },
        { value: "bottom-left", label: "Bottom-Left Pill", desc: "Floating rounded pill button at bottom-left corner" },
        { value: "top-right", label: "Top-Right Trigger", desc: "Compact trigger pill placed near top-right header" },
      ],

      // Modals active states
      activeTriageReport: null,
      activeLightboxUrl: "",
      activeVideoUrl: "",

      // Sandbox Form & Interactive Annotation State
      sandboxForm: {
        title: "",
        description: "",
        issue_type: "bug",
        severity: "medium",
        reporter_name: "QA Tester",
        reporter_email: "tester@vertex.com",
      },
      annoTool: "pen",
      annoColor: "#ef4444",
      isSandboxDrawing: false,
      sandboxStartX: 0,
      sandboxStartY: 0,
      sandboxLastX: 0,
      sandboxLastY: 0,
      sandboxHistory: [],
      sandboxTextOverlay: {
        visible: false,
        text: "",
        canvasX: 0,
        canvasY: 0,
        cssLeft: 0,
        cssTop: 0,
        isShapeLabel: false,
      },
    };
  },

  computed: {
    currentPlatform() {
      return this.platforms.find((p) => p.key === this.selectedPlatformKey) || this.platforms[0] || null;
    },

    apiBaseUrl() {
      let base = (this.$axios && this.$axios.defaults && this.$axios.defaults.baseURL) 
        ? this.$axios.defaults.baseURL 
        : (process.env.VUE_APP_API_BASE_URL || window.location.origin);
      if (base.endsWith("/")) base = base.slice(0, -1);
      return base;
    },

    filteredPlatforms() {
      if (!this.platformSearchQuery.trim()) return this.platforms;
      const q = this.platformSearchQuery.toLowerCase();
      return this.platforms.filter((p) => {
        return (
          (p.name || "").toLowerCase().includes(q) ||
          (p.key || "").toLowerCase().includes(q) ||
          (p.description || "").toLowerCase().includes(q)
        );
      });
    },

    totalOpenBugsCount() {
      return this.platforms.reduce((acc, p) => acc + (p.open_bugs_count ?? p.open_bugs ?? 0), 0);
    },

    totalResolvedBugsCount() {
      return this.platforms.reduce((acc, p) => acc + (p.resolved_bugs_count ?? p.resolved_bugs ?? 0), 0);
    },

    filteredBugReports() {
      return this.bugReports.filter((r) => {
        if (this.selectedStatusFilter && r.status !== this.selectedStatusFilter) return false;
        if (this.selectedTypeFilter && r.issue_type !== this.selectedTypeFilter) return false;
        if (this.selectedSeverityFilter && r.severity !== this.selectedSeverityFilter) return false;
        if (this.bugSearchQuery.trim()) {
          const q = this.bugSearchQuery.toLowerCase();
          return (
            (r.title || "").toLowerCase().includes(q) ||
            (r.description || "").toLowerCase().includes(q) ||
            (r.reporter_name || "").toLowerCase().includes(q) ||
            (r.reporter_email || "").toLowerCase().includes(q)
          );
        }
        return true;
      });
    },

    scriptSnippetCode() {
      const pId = this.currentPlatform ? this.currentPlatform.id : 1;
      const uuid = this.widgetForm.widget_uuid || "WIDGET-UUID";
      const pos = this.widgetForm.widget_position || "right-edge";
      return `<script 
  src="${this.apiBaseUrl}/api/public/bug-widget/widget.js" 
  data-platform-id="${pId}" 
  data-widget-uuid="${uuid}" 
  data-position="${pos}"
  async>` + "\n</" + "script>";
    },

    iframeSnippetCode() {
      const pKey = this.selectedPlatformKey || "spark";
      const uuid = this.widgetForm.widget_uuid || "WIDGET-UUID";
      const pos = this.widgetForm.widget_position || "right-edge";
      const theme = this.widgetForm.widget_theme || "light";
      return `<iframe 
  src="${this.apiBaseUrl}/api/public/bug-widget/${pKey}/embed?uuid=${uuid}&position=${pos}&theme=${theme}" 
  width="100%" 
  height="600" 
  frameborder="0" 
  style="border-radius: 8px; border: 1px solid #e2e8f0;">
</iframe>`;
    },

    curlSnippetCode() {
      const pKey = this.selectedPlatformKey || "spark";
      return `// REST API Bug Submission Example:
curl -X POST "${this.apiBaseUrl}/api/public/bug-widget/${pKey}/submit" \\
  -H "Content-Type: application/json" \\
  -d '{
    "widget_uuid": "${this.widgetForm.widget_uuid || 'YOUR_WIDGET_UUID'}",
    "title": "Unresponsive navigation button",
    "description": "User unable to click navigation dropdown in Chrome",
    "issue_type": "bug",
    "severity": "high",
    "reporter_name": "QA Engine",
    "reporter_email": "qa@vertex.com",
    "page_url": "https://app.example.com/checkout",
    "user_agent": "Mozilla/5.0 Chrome/120.0.0.0",
    "viewport": "1920x1080"
  }'`;
    },
  },

  watch: {
    embeddedPlatform: {
      immediate: true,
      handler(newVal) {
        if (this.embeddedMode && newVal) {
          this.currentView = "platform";
          if (newVal.key) {
            this.switchPlatform(newVal.key);
          }
        }
      },
    },
    currentPlatform: {
      immediate: true,
      handler(p) {
        if (p) {
          this.widgetForm.widget_position = p.widget_position || "right-edge";
          this.widgetForm.widget_button_text = p.widget_button_text || "Report a Bug";
          this.widgetForm.widget_theme = p.widget_theme || "light";
          this.widgetForm.primary_color = p.primary_color || "#00a0c1";
          this.widgetForm.widget_enabled = p.widget_enabled !== false;
          this.widgetForm.widget_uuid = p.widget_uuid || "";
          this.widgetForm.jira_domain = p.jira_domain || "vertex.atlassian.net";
          this.widgetForm.jira_project_key = p.jira_project_key || "DEV";
          this.widgetForm.jira_email = p.jira_email || "";
          this.widgetForm.jira_auto_sync = !!p.jira_auto_sync;
          this.widgetForm.ai_auto_fix = p.ai_auto_fix !== false;
          this.widgetForm.bitbucket_workspace = p.bitbucket_workspace || "VertexSecurity";
          if (Array.isArray(p.repositories) && p.repositories.length > 0) {
            this.widgetForm.repositories = p.repositories.map((r) => ({
              role: r.role || "repo",
              workspace: r.workspace || p.bitbucket_workspace || "VertexSecurity",
              repo_slug: r.repo_slug || "",
              repo_url: r.repo_url || "",
              branch: r.branch || "dev",
            }));
          } else {
            this.widgetForm.repositories = this.defaultRepositoriesForKey(
              p.key,
              p.bitbucket_workspace || "VertexSecurity"
            );
          }
        }
      },
    },
    widgetForm: {
      deep: true,
      handler(val) {
        if (this.currentPlatform && (this.currentPlatform.key === "spark" || this.selectedPlatformKey === "spark")) {
          window.dispatchEvent(
            new CustomEvent("spark-bug-widget-updated", {
              detail: {
                platform_key: "spark",
                widget_position: val.widget_position,
                widget_button_text: val.widget_button_text,
                widget_theme: val.widget_theme,
                widget_enabled: val.widget_enabled,
                primary_color: val.primary_color,
                widget_uuid: val.widget_uuid,
              },
            })
          );
        }
      },
    },
  },

  mounted() {
    this.fetchPlatforms();
    this.fetchUserJiraCredential();
    this.fetchBitbucketRepositories();
    this.onBugReportSubmitted = () => {
      this.fetchPlatforms();
      if (this.currentView === "platform") {
        this.fetchBugReports();
      }
    };
    this.onUserJiraCredUpdated = () => {
      this.fetchUserJiraCredential();
    };
    window.addEventListener("spark-bug-report-submitted", this.onBugReportSubmitted);
    window.addEventListener("spark-user-jira-cred-updated", this.onUserJiraCredUpdated);
  },

  beforeUnmount() {
    if (this.onBugReportSubmitted) {
      window.removeEventListener("spark-bug-report-submitted", this.onBugReportSubmitted);
    }
    if (this.onUserJiraCredUpdated) {
      window.removeEventListener("spark-user-jira-cred-updated", this.onUserJiraCredUpdated);
    }
  },

  methods: {
    getLoggedInUserId() {
      const storeId = this.$store?.state?.auth?.currentUser?.id;
      if (storeId) return storeId;
      try {
        const userStr = localStorage.getItem("user") || sessionStorage.getItem("user");
        if (userStr) {
          const parsed = JSON.parse(userStr);
          if (parsed && parsed.id) return parsed.id;
        }
      } catch (e) {
        // ignore storage parse error
      }
      return 0;
    },

    getLoggedInUserEmail() {
      const storeEmail = this.$store?.state?.auth?.currentUser?.email;
      if (storeEmail) return storeEmail;
      try {
        const userStr = localStorage.getItem("user") || sessionStorage.getItem("user");
        if (userStr) {
          const parsed = JSON.parse(userStr);
          if (parsed && (parsed.email || parsed.user_email || parsed.username)) {
            return parsed.email || parsed.user_email || parsed.username;
          }
        }
      } catch (e) {
        // ignore storage parse error
      }
      return localStorage.getItem("user_email") || "";
    },

    fetchUserJiraCredential() {
      const userId = this.getLoggedInUserId();
      const email = this.getLoggedInUserEmail();
      const params = {};
      if (userId) params.user_id = userId;
      if (email) params.user_email = email;
      this.$axios
        .get("/api/platform-bug-reporting/user-jira-credentials", { params })
        .then((res) => {
          if (res.data && res.data.success) {
            const d = res.data.data || res.data;
            this.userJiraCred.has_personal_token = !!d.has_personal_token;
            this.userJiraCred.jira_domain = d.jira_domain || "vertex.atlassian.net";
            this.userJiraCred.jira_email = d.jira_email || email || "";
            this.userJiraCred.jira_display_name = d.jira_display_name || "";
            this.userJiraCred.masked_token = d.masked_token || "";
            this.userJiraCred.jira_api_token = "";
          }
        })
        .catch(() => {
          // silently ignore if not configured yet
        });
    },

    openUserJiraModal() {
      this.$router.push("/platform-settings/user-settings");
    },

    saveUserJiraCredential() {
      if (!this.userJiraCred.jira_email) {
        this.notify("error", "Please enter your Atlassian account email.");
        return;
      }
      if (!this.userJiraCred.jira_api_token && !this.userJiraCred.has_personal_token) {
        this.notify("error", "Please enter your personal Jira API token.");
        return;
      }
      this.userJiraCred.isSaving = true;
      const payload = {
        user_id: this.getLoggedInUserId(),
        user_email: this.getLoggedInUserEmail() || this.userJiraCred.jira_email,
        jira_domain: this.userJiraCred.jira_domain || "vertex.atlassian.net",
        jira_email: this.userJiraCred.jira_email,
        jira_api_token: this.userJiraCred.jira_api_token,
      };

      this.$axios
        .post("/api/platform-bug-reporting/user-jira-credentials", payload)
        .then((res) => {
          if (res.data && res.data.success) {
            const d = res.data.data || res.data || {};
            this.userJiraCred.has_personal_token = true;
            this.userJiraCred.jira_domain = d.jira_domain || payload.jira_domain;
            this.userJiraCred.jira_email = d.jira_email || payload.jira_email;
            this.userJiraCred.jira_display_name = d.jira_display_name || "";
            this.userJiraCred.masked_token = d.masked_token || "••••••••";
            this.userJiraCred.jira_api_token = "";
            this.notify("success", res.data.message || "Personal Jira credentials verified and saved!");
            this.closeModalSafe("userJiraCredModal");
            window.dispatchEvent(
              new CustomEvent("spark-user-jira-cred-updated", {
                detail: { ...this.userJiraCred },
              })
            );
          } else {
            this.notify("error", (res.data && res.data.message) || "Failed to verify Jira credentials.");
          }
        })
        .catch((err) => {
          const msg = (err.response && err.response.data && err.response.data.message) || "Could not verify Jira token. Please check your email and API token.";
          this.notify("error", msg);
        })
        .finally(() => {
          this.userJiraCred.isSaving = false;
        });
    },

    deleteUserJiraCredential() {
      this.userJiraCred.isSaving = true;
      const userId = this.getLoggedInUserId();
      const email = this.getLoggedInUserEmail() || this.userJiraCred.jira_email;
      const params = { user_email: email };
      if (userId) params.user_id = userId;
      this.$axios
        .delete("/api/platform-bug-reporting/user-jira-credentials", { params })
        .then((res) => {
          if (res.data && res.data.success) {
            this.userJiraCred.has_personal_token = false;
            this.userJiraCred.jira_display_name = "";
            this.userJiraCred.masked_token = "";
            this.userJiraCred.jira_api_token = "";
            this.notify("success", "Personal Jira token removed. Manual pushes will use the general token.");
            window.dispatchEvent(
              new CustomEvent("spark-user-jira-cred-updated", {
                detail: { ...this.userJiraCred },
              })
            );
          }
        })
        .catch(() => {
          this.notify("error", "Failed to remove personal Jira token.");
        })
        .finally(() => {
          this.userJiraCred.isSaving = false;
        });
    },

    fetchPlatforms() {
      this.isLoadingPlatforms = true;
      this.$axios
        .get("/api/platform-bug-reporting/platforms")
        .then((res) => {
          if (res.data && res.data.success) {
            this.platforms = res.data.data || [];
            if (this.embeddedMode && this.embeddedPlatform && this.embeddedPlatform.key) {
              this.selectedPlatformKey = this.embeddedPlatform.key;
            } else if (this.platforms.length > 0 && !this.platforms.some((p) => p.key === this.selectedPlatformKey)) {
              this.selectedPlatformKey = this.platforms[0].key;
            }
            if (this.currentView === "platform") {
              this.fetchBugReports();
            }
          }
        })
        .catch(() => {
          this.$axios
            .get("/api/platform-guide/platforms")
            .then((res) => {
              if (res.data && res.data.success) {
                this.platforms = res.data.data || [];
                if (this.embeddedMode && this.embeddedPlatform && this.embeddedPlatform.key) {
                  this.selectedPlatformKey = this.embeddedPlatform.key;
                } else if (this.platforms.length > 0 && !this.platforms.some((p) => p.key === this.selectedPlatformKey)) {
                  this.selectedPlatformKey = this.platforms[0].key;
                }
                if (this.currentView === "platform") {
                  this.fetchBugReports();
                }
              }
            })
            .catch((err) => {
              console.error("Error fetching platforms:", err);
            });
        })
        .finally(() => {
          this.isLoadingPlatforms = false;
        });
    },

    selectPlatformView(plat) {
      if (!plat) return;
      this.selectedPlatformKey = plat.key;
      this.currentView = "platform";
      this.activeTab = "reports";
      this.fetchBugReports();
    },

    switchPlatform(key) {
      this.selectedPlatformKey = key;
      this.fetchBugReports();
    },

    fetchBugReports() {
      if (!this.selectedPlatformKey) return;
      this.isLoadingBugReports = true;
      const key = encodeURIComponent(this.selectedPlatformKey);
      const params = {};
      if (this.selectedStatusFilter) params.status = this.selectedStatusFilter;
      if (this.selectedTypeFilter) params.issue_type = this.selectedTypeFilter;
      if (this.selectedSeverityFilter) params.severity = this.selectedSeverityFilter;
      if (this.bugSearchQuery.trim()) params.search = this.bugSearchQuery.trim();

      this.$axios
        .get(`/api/platform-bug-reporting/reports?platform_key=${key}`, { params })
        .then((res) => {
          if (res.data && res.data.success) {
            this.bugReports = res.data.data || [];
          } else {
            this.bugReports = [];
          }
        })
        .catch(() => {
          const platformId = this.currentPlatform ? this.currentPlatform.id : null;
          if (platformId) {
            this.$axios
              .get(`/api/bug-reporting/platforms/${platformId}/reports`, { params })
              .then((res) => {
                if (res.data && res.data.success) {
                  this.bugReports = res.data.data || [];
                } else {
                  this.bugReports = [];
                }
              })
              .catch((err) => {
                console.error("Error fetching bug reports:", err);
                this.bugReports = [];
              });
          } else {
            this.bugReports = [];
          }
        })
        .finally(() => {
          this.isLoadingBugReports = false;
        });
    },

    updateReportStatus(report, newStatus) {
      this.isSubmitting = true;
      this.$axios
        .put(`/api/platform-bug-reporting/reports/${report.id}`, { status: newStatus })
        .then((res) => {
          if (res.data && res.data.success) {
            report.status = newStatus;
            this.notify("success", `Bug report status updated to ${newStatus.toUpperCase()}`);
          }
        })
        .catch(() => {
          this.$axios
            .put(`/api/bug-reporting/reports/${report.id}`, { status: newStatus })
            .then((res) => {
              if (res.data && res.data.success) {
                report.status = newStatus;
                this.notify("success", `Bug report status updated to ${newStatus.toUpperCase()}`);
              }
            })
            .catch((err) => {
              console.error("Error updating report status:", err);
              this.notify("error", "Failed to update report status.");
            });
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },

    pushReportToJira(report, force = false) {
      if (!report || !report.id) return;
      this.pushingJiraId = report.id;
      const payload = {
        id: report.id,
        jira_domain: this.widgetForm.jira_domain || "vertex.atlassian.net",
        jira_project_key: this.widgetForm.jira_project_key || "DEV",
        jira_email: this.widgetForm.jira_email || "",
        user_id: this.getLoggedInUserId(),
        user_email: this.getLoggedInUserEmail() || this.userJiraCred.jira_email || "",
        force: !!force,
      };

      const handleSuccess = (res) => {
        if (res.data && res.data.success) {
          const updated = res.data.data || {};
          const issueKey = res.data.jira_issue_key || updated.jira_issue_key;
          const issueUrl = res.data.jira_issue_url || updated.jira_issue_url;
          const pushedBy = res.data.pushed_by || updated.jira_pushed_by || "";
          report.jira_issue_key = issueKey;
          report.jira_issue_url = issueUrl;
          report.jira_project_key = updated.jira_project_key || payload.jira_project_key;
          report.jira_pushed_by = pushedBy;
          report.jira_synced_at = updated.jira_synced_at || new Date().toISOString();
          if (this.activeTriageReport && this.activeTriageReport.id === report.id) {
            this.activeTriageReport.jira_issue_key = issueKey;
            this.activeTriageReport.jira_issue_url = issueUrl;
            this.activeTriageReport.jira_project_key = report.jira_project_key;
            this.activeTriageReport.jira_pushed_by = pushedBy;
            this.activeTriageReport.jira_synced_at = report.jira_synced_at;
          }
          const idx = this.bugReports.findIndex((r) => r.id === report.id);
          if (idx !== -1) {
            this.bugReports[idx].jira_issue_key = issueKey;
            this.bugReports[idx].jira_issue_url = issueUrl;
            this.bugReports[idx].jira_project_key = report.jira_project_key;
            this.bugReports[idx].jira_pushed_by = pushedBy;
            this.bugReports[idx].jira_synced_at = report.jira_synced_at;
          }
          this.notify("success", res.data.message || `Pushed to Jira: ${issueKey}`);
        } else {
          this.notify("error", (res.data && res.data.message) || "Failed to push bug report to Jira.");
        }
      };

      this.$axios
        .post(`/api/platform-bug-reporting/reports/${report.id}/push-jira`, payload)
        .then(handleSuccess)
        .catch(() => {
          this.$axios
            .post(`/api/bug-reporting/reports/${report.id}/push-jira`, payload)
            .then(handleSuccess)
            .catch((err) => {
              console.error("Error pushing bug report to Jira:", err);
              const errMsg = (err.response && err.response.data && err.response.data.message) || "Failed to push bug report to Jira.";
              this.notify("error", errMsg);
            });
        })
        .finally(() => {
          this.pushingJiraId = null;
        });
    },

    unlinkJiraFromReport(report) {
      if (!report || !report.id) return;
      this.pushingJiraId = report.id;
      const payload = {
        id: report.id,
        unlink: true,
      };

      const handleSuccess = (res) => {
        if (res.data && res.data.success) {
          report.jira_issue_key = "";
          report.jira_issue_url = "";
          report.jira_project_key = "";
          report.jira_pushed_by = "";
          report.jira_synced_at = null;
          if (this.activeTriageReport && this.activeTriageReport.id === report.id) {
            this.activeTriageReport.jira_issue_key = "";
            this.activeTriageReport.jira_issue_url = "";
            this.activeTriageReport.jira_project_key = "";
            this.activeTriageReport.jira_pushed_by = "";
            this.activeTriageReport.jira_synced_at = null;
          }
          const idx = this.bugReports.findIndex((r) => r.id === report.id);
          if (idx !== -1) {
            this.bugReports[idx].jira_issue_key = "";
            this.bugReports[idx].jira_issue_url = "";
            this.bugReports[idx].jira_project_key = "";
            this.bugReports[idx].jira_pushed_by = "";
            this.bugReports[idx].jira_synced_at = null;
          }
          this.notify("success", res.data.message || "Unlinked Jira ticket from bug report.");
        } else {
          this.notify("error", (res.data && res.data.message) || "Failed to unlink Jira ticket.");
        }
      };

      this.$axios
        .post(`/api/platform-bug-reporting/reports/${report.id}/push-jira`, payload)
        .then(handleSuccess)
        .catch(() => {
          this.$axios
            .post(`/api/bug-reporting/reports/${report.id}/push-jira`, payload)
            .then(handleSuccess)
            .catch(() => {
              this.notify("error", "Failed to unlink Jira ticket.");
            });
        })
        .finally(() => {
          this.pushingJiraId = null;
        });
    },

    defaultRepositoriesForKey(key, workspace = "VertexSecurity") {
      const ws = (workspace || "VertexSecurity").trim() || "VertexSecurity";
      const k = (key || "").toLowerCase().trim();
      if (k === "spark") {
        return [
          { role: "be", workspace: ws, repo_slug: "spark_go", repo_url: `https://bitbucket.org/${ws}/spark_go`, branch: "dev" },
          { role: "fe", workspace: ws, repo_slug: "spark_vuejs", repo_url: `https://bitbucket.org/${ws}/spark_vuejs`, branch: "dev" },
        ];
      }
      if (k === "xsurflog") {
        return [
          { role: "be", workspace: ws, repo_slug: "xsurflog_be", repo_url: `https://bitbucket.org/${ws}/xsurflog_be`, branch: "dev" },
          { role: "fe", workspace: ws, repo_slug: "xsurflog_fe", repo_url: `https://bitbucket.org/${ws}/xsurflog_fe`, branch: "dev" },
          { role: "extension", workspace: ws, repo_slug: "xsurflog_extension", repo_url: `https://bitbucket.org/${ws}/xsurflog_extension`, branch: "dev" },
          { role: "user-agent", workspace: ws, repo_slug: "xsurflog_user_agent", repo_url: `https://bitbucket.org/${ws}/xsurflog_user_agent`, branch: "dev" },
        ];
      }
      return [];
    },

    getPlatformRepositories(plat) {
      if (!plat) return [];
      if (Array.isArray(plat.repositories) && plat.repositories.length > 0) {
        return plat.repositories;
      }
      return this.defaultRepositoriesForKey(plat.key, plat.bitbucket_workspace);
    },

    getPlatformRepoCount(plat) {
      return this.getPlatformRepositories(plat).length;
    },

    getPlatformReposTooltip(plat) {
      const repos = this.getPlatformRepositories(plat);
      if (!repos.length) return "No Bitbucket repositories connected";
      return (
        "Connected Repositories:\n" +
        repos
          .map((r) => `• ${r.workspace || "VertexSecurity"}/${r.repo_slug} (${(r.role || "repo").toUpperCase()})`)
          .join("\n")
      );
    },

    fetchBitbucketRepositories(workspace) {
      const ws =
        (workspace ||
          this.widgetForm.bitbucket_workspace ||
          this.platformForm.bitbucket_workspace ||
          "VertexSecurity").trim() || "VertexSecurity";
      this.isLoadingBitbucketRepos = true;

      const handleSuccess = (res) => {
        if (res.data && res.data.success && Array.isArray(res.data.data)) {
          this.bitbucketRepoOptions = res.data.data.map((r) => {
            const detectedRole = r.suggested_role || r.role || "repo";
            return {
              role: detectedRole,
              suggested_role: detectedRole,
              workspace: r.workspace || ws,
              repo_slug: r.repo_slug,
              full_name: r.full_name || `${r.workspace || ws}/${r.repo_slug}`,
              repo_url: r.repo_url || `https://bitbucket.org/${r.workspace || ws}/${r.repo_slug}`,
              branch: r.branch || "dev",
            };
          });
        }
      };

      this.$axios
        .get("/api/platform-bug-reporting/bitbucket/repositories", { params: { workspace: ws } })
        .then(handleSuccess)
        .catch(() => {
          this.$axios
            .get("/api/bug-reporting/bitbucket/repositories", { params: { workspace: ws } })
            .then(handleSuccess)
            .catch(() => {
              // Keep seeded fallback catalog if API unreachable
            });
        })
        .finally(() => {
          this.isLoadingBitbucketRepos = false;
        });
    },

    isRepoAttached(target, slug) {
      if (!slug) return false;
      const formRef =
        target === "widget"
          ? this.widgetForm
          : target === "create"
          ? this.platformForm
          : this.editPlatformForm;
      if (!formRef || !Array.isArray(formRef.repositories)) return false;
      const s = slug.toLowerCase().trim();
      return formRef.repositories.some((r) => (r.repo_slug || "").toLowerCase().trim() === s);
    },

    onRepoDropdownSelect(target) {
      const picker = this.repoPicker[target];
      if (!picker || !picker.selectedSlug || picker.selectedSlug === "__custom__") return;
      const found = this.bitbucketRepoOptions.find((opt) => opt.repo_slug === picker.selectedSlug);
      if (found) {
        picker.role = found.suggested_role || found.role || "repo";
      }
      this.addRepositoryToTarget(target);
    },

    addRepositoryToTarget(target) {
      const picker = this.repoPicker[target];
      if (!picker) return;

      const formRef =
        target === "widget"
          ? this.widgetForm
          : target === "create"
          ? this.platformForm
          : this.editPlatformForm;
      if (!formRef) return;

      const rawSlug =
        picker.selectedSlug === "__custom__" ? picker.customSlug : picker.selectedSlug;
      let slug = (rawSlug || "").trim();
      if (!slug) return;

      let ws = (formRef.bitbucket_workspace || "VertexSecurity").trim() || "VertexSecurity";
      if (slug.includes("/")) {
        const parts = slug.split("/").filter(Boolean);
        if (parts.length >= 2) {
          ws = parts[parts.length - 2];
          slug = parts[parts.length - 1];
        }
      }
      slug = slug.replace(/\.git$/i, "");

      if (!Array.isArray(formRef.repositories)) {
        formRef.repositories = [];
      }

      const foundOpt = this.bitbucketRepoOptions.find(
        (o) => (o.repo_slug || "").toLowerCase() === slug.toLowerCase()
      );
      const role = (picker.role || (foundOpt && (foundOpt.suggested_role || foundOpt.role)) || "repo").toLowerCase();
      const exists = formRef.repositories.some(
        (r) => (r.repo_slug || "").toLowerCase() === slug.toLowerCase()
      );
      if (exists) {
        this.notify("info", `Repository ${ws}/${slug} is already attached.`);
        picker.selectedSlug = "";
        picker.customSlug = "";
        return;
      }

      formRef.repositories.push({
        role,
        workspace: ws,
        repo_slug: slug,
        repo_url: `https://bitbucket.org/${ws}/${slug}`,
        branch: (foundOpt && foundOpt.branch) || "dev",
      });

      if (!foundOpt) {
        this.bitbucketRepoOptions.push({
          role,
          suggested_role: role,
          workspace: ws,
          repo_slug: slug,
          full_name: `${ws}/${slug}`,
          repo_url: `https://bitbucket.org/${ws}/${slug}`,
          branch: "dev",
        });
      }

      picker.selectedSlug = "";
      picker.customSlug = "";
    },

    removeRepositoryFromTarget(target, idx) {
      const formRef =
        target === "widget"
          ? this.widgetForm
          : target === "create"
          ? this.platformForm
          : this.editPlatformForm;
      if (!formRef || !Array.isArray(formRef.repositories)) return;
      formRef.repositories.splice(idx, 1);
    },

    saveWidgetSettings() {
      if (!this.currentPlatform) return;
      this.isSubmitting = true;
      const key = encodeURIComponent(this.currentPlatform.key);
      const ws = (this.widgetForm.bitbucket_workspace || "VertexSecurity").trim() || "VertexSecurity";
      const normalizedRepos = (this.widgetForm.repositories || []).map((r) => ({
        role: (r.role || "repo").toLowerCase(),
        workspace: r.workspace || ws,
        repo_slug: r.repo_slug,
        repo_url: r.repo_url || `https://bitbucket.org/${r.workspace || ws}/${r.repo_slug}`,
        branch: "dev",
      }));
      const payload = {
        platform_id: this.currentPlatform.id,
        platform_key: this.currentPlatform.key,
        widget_position: this.widgetForm.widget_position,
        widget_button_text: this.widgetForm.widget_button_text,
        widget_theme: this.widgetForm.widget_theme,
        widget_enabled: this.widgetForm.widget_enabled,
        primary_color: this.widgetForm.primary_color,
        regenerate_widget_uuid: !!this.widgetForm.regenerate_uuid,
        regenerate_uuid: !!this.widgetForm.regenerate_uuid,
        jira_domain: this.widgetForm.jira_domain || "vertex.atlassian.net",
        jira_project_key: this.widgetForm.jira_project_key || "DEV",
        jira_email: this.widgetForm.jira_email || "",
        jira_auto_sync: !!this.widgetForm.jira_auto_sync,
        ai_auto_fix: this.widgetForm.ai_auto_fix !== false,
        bitbucket_workspace: ws,
        repositories: normalizedRepos,
      };

      this.$axios
        .put(`/api/platform-bug-reporting/widget-settings/${key}`, payload)
        .then((res) => {
          if (res.data && res.data.success) {
            this.notify("success", "Widget & repository settings updated successfully!");
            if (res.data.data && res.data.data.widget_uuid) {
              this.widgetForm.widget_uuid = res.data.data.widget_uuid;
            }
            if (this.currentPlatform && (this.currentPlatform.key === 'spark' || this.selectedPlatformKey === 'spark')) {
              window.dispatchEvent(
                new CustomEvent('spark-bug-widget-updated', {
                  detail: {
                    platform_key: 'spark',
                    widget_position: this.widgetForm.widget_position,
                    widget_button_text: this.widgetForm.widget_button_text,
                    widget_theme: this.widgetForm.widget_theme,
                    widget_enabled: this.widgetForm.widget_enabled,
                    primary_color: this.widgetForm.primary_color,
                    widget_uuid: this.widgetForm.widget_uuid,
                  },
                })
              );
            }
            this.fetchPlatforms();
          }
        })
        .catch(() => {
          this.$axios
            .put(`/api/bug-reporting/platforms/${this.currentPlatform.id}/settings`, payload)
            .then((res) => {
              if (res.data && res.data.success) {
                this.notify("success", "Widget & repository settings updated successfully!");
                if (res.data.data && res.data.data.widget_uuid) {
                  this.widgetForm.widget_uuid = res.data.data.widget_uuid;
                }
                if (this.currentPlatform && (this.currentPlatform.key === 'spark' || this.selectedPlatformKey === 'spark')) {
                  window.dispatchEvent(
                    new CustomEvent('spark-bug-widget-updated', {
                      detail: {
                        platform_key: 'spark',
                        widget_position: this.widgetForm.widget_position,
                        widget_button_text: this.widgetForm.widget_button_text,
                        widget_theme: this.widgetForm.widget_theme,
                        widget_enabled: this.widgetForm.widget_enabled,
                        primary_color: this.widgetForm.primary_color,
                        widget_uuid: this.widgetForm.widget_uuid,
                      },
                    })
                  );
                }
                this.fetchPlatforms();
              }
            })
            .catch((err) => {
              console.error("Error saving widget settings:", err);
              this.notify("error", "Failed to update widget settings.");
            });
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },

    openAddPlatformModal() {
      this.platformForm = {
        name: "",
        key: "",
        description: "",
        icon: "fa-solid fa-bug",
        primary_color: "#00a0c1",
        encoded_icon: "",
        icon_filename: "",
        jira_domain: "vertex.atlassian.net",
        jira_project_key: "DEV",
        jira_auto_sync: false,
        ai_auto_fix: true,
        bitbucket_workspace: "VertexSecurity",
        repositories: [],
      };
      this.repoPicker.create = { selectedSlug: "", customSlug: "", role: "be" };
      this.openModalSafe("platformFormModal");
    },

    onPlatformNameInput() {
      if (this.platformForm.name) {
        this.platformForm.key = this.platformForm.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
        if (!this.platformForm.repositories || this.platformForm.repositories.length === 0) {
          const defaults = this.defaultRepositoriesForKey(
            this.platformForm.key,
            this.platformForm.bitbucket_workspace || "VertexSecurity"
          );
          if (defaults.length > 0) {
            this.platformForm.repositories = defaults;
          }
        }
      }
    },

    saveNewPlatform() {
      if (!this.platformForm.name || !this.platformForm.key) return;
      if (this.platformForm.jira_project_key) {
        this.platformForm.jira_project_key = this.platformForm.jira_project_key.trim().toUpperCase();
      }
      this.isSubmitting = true;
      this.$axios
        .post("/api/platform-bug-reporting/platforms", this.platformForm)
        .then((res) => {
          if (res.data && res.data.success) {
            this.notify("success", "New platform registered successfully!");
            this.closeModalSafe("platformFormModal");
            this.fetchPlatforms();
          }
        })
        .catch(() => {
          this.$axios
            .post("/api/platform-guide/platforms", this.platformForm)
            .then((res) => {
              if (res.data && res.data.success) {
                this.notify("success", "New platform registered successfully!");
                this.closeModalSafe("platformFormModal");
                this.fetchPlatforms();
              }
            })
            .catch((err) => {
              console.error("Error creating platform:", err);
              this.notify("error", "Failed to register platform.");
            });
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },

    openEditPlatformModal(plat) {
      const ws = plat.bitbucket_workspace || "VertexSecurity";
      const repos =
        Array.isArray(plat.repositories) && plat.repositories.length > 0
          ? plat.repositories.map((r) => ({
              role: r.role || "repo",
              workspace: r.workspace || ws,
              repo_slug: r.repo_slug || "",
              repo_url: r.repo_url || `https://bitbucket.org/${r.workspace || ws}/${r.repo_slug}`,
              branch: "dev",
            }))
          : this.defaultRepositoriesForKey(plat.key, ws);

      this.editPlatformForm = {
        id: plat.id,
        name: plat.name,
        key: plat.key,
        description: plat.description,
        primary_color: plat.primary_color || "#00a0c1",
        jira_domain: plat.jira_domain || "vertex.atlassian.net",
        jira_project_key: plat.jira_project_key || "DEV",
        jira_auto_sync: !!plat.jira_auto_sync,
        ai_auto_fix: plat.ai_auto_fix !== false,
        bitbucket_workspace: ws,
        repositories: repos,
      };
      this.repoPicker.edit = { selectedSlug: "", customSlug: "", role: "be" };
      this.openModalSafe("editPlatformModal");
    },

    saveEditPlatform() {
      if (this.editPlatformForm.jira_project_key) {
        this.editPlatformForm.jira_project_key = this.editPlatformForm.jira_project_key.trim().toUpperCase();
      }
      this.isSubmitting = true;
      this.$axios
        .put(`/api/platform-bug-reporting/platforms/${this.editPlatformForm.id}`, this.editPlatformForm)
        .then((res) => {
          if (res.data && res.data.success) {
            if (this.currentPlatform && this.currentPlatform.id === this.editPlatformForm.id) {
              this.widgetForm.jira_domain = this.editPlatformForm.jira_domain;
              this.widgetForm.jira_project_key = this.editPlatformForm.jira_project_key;
              this.widgetForm.jira_auto_sync = this.editPlatformForm.jira_auto_sync;
              this.widgetForm.ai_auto_fix = this.editPlatformForm.ai_auto_fix;
              this.widgetForm.bitbucket_workspace = this.editPlatformForm.bitbucket_workspace || "VertexSecurity";
              this.widgetForm.repositories = [...(this.editPlatformForm.repositories || [])];
            }
            this.notify("success", "Platform settings saved!");
            this.closeModalSafe("editPlatformModal");
            this.fetchPlatforms();
          }
        })
        .catch(() => {
          this.$axios
            .put(`/api/platform-guide/platforms/${this.editPlatformForm.id}`, this.editPlatformForm)
            .then((res) => {
              if (res.data && res.data.success) {
                if (this.currentPlatform && this.currentPlatform.id === this.editPlatformForm.id) {
                  this.widgetForm.jira_domain = this.editPlatformForm.jira_domain;
                  this.widgetForm.jira_project_key = this.editPlatformForm.jira_project_key;
                  this.widgetForm.jira_auto_sync = this.editPlatformForm.jira_auto_sync;
                  this.widgetForm.ai_auto_fix = this.editPlatformForm.ai_auto_fix;
                  this.widgetForm.bitbucket_workspace = this.editPlatformForm.bitbucket_workspace || "VertexSecurity";
                  this.widgetForm.repositories = [...(this.editPlatformForm.repositories || [])];
                }
                this.notify("success", "Platform settings saved!");
                this.closeModalSafe("editPlatformModal");
                this.fetchPlatforms();
              }
            })
            .catch((err) => {
              console.error("Error updating platform:", err);
              this.notify("error", "Failed to update platform.");
            });
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },

    confirmDeletePlatform(plat) {
      this.platformToDelete = plat;
      this.openModalSafe("deletePlatformModal");
    },

    deletePlatform() {
      if (!this.platformToDelete) return;
      this.isSubmitting = true;
      this.$axios
        .delete(`/api/platform-bug-reporting/platforms/${this.platformToDelete.id}`)
        .then((res) => {
          if (res.data && res.data.success) {
            this.notify("success", "Platform deleted successfully!");
            this.closeModalSafe("deletePlatformModal");
            this.fetchPlatforms();
          }
        })
        .catch(() => {
          this.$axios
            .delete(`/api/platform-guide/platforms/${this.platformToDelete.id}`)
            .then((res) => {
              if (res.data && res.data.success) {
                this.notify("success", "Platform deleted successfully!");
                this.closeModalSafe("deletePlatformModal");
                this.fetchPlatforms();
              }
            })
            .catch((err) => {
              console.error("Error deleting platform:", err);
              this.notify("error", "Failed to delete platform.");
            });
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },

    openTriageModal(report) {
      this.activeTriageReport = {
        workflow_stage: report.workflow_stage || (report.jira_issue_key ? "jira_logged" : "reported"),
        ai_status: report.ai_status || "idle",
        ai_verdict: report.ai_verdict || "",
        ai_analysis: report.ai_analysis || "",
        ai_branch: report.ai_branch || "dev",
        ai_commit_hash: report.ai_commit_hash || "",
        ai_files_changed: report.ai_files_changed || "",
        ...report,
      };
      if (!this.activeTriageReport.ai_branch) {
        this.activeTriageReport.ai_branch = "dev";
      }
      if (!this.activeTriageReport.workflow_stage) {
        this.activeTriageReport.workflow_stage = report.jira_issue_key ? "jira_logged" : "reported";
      }
      this.openModalSafe("triageModal");
    },

    getWorkflowStageLabel(stage) {
      const map = {
        reported: "Reported",
        jira_logged: "Jira Logged",
        ai_triaging: "AI Triaging",
        local_dev: "Local Dev Fix",
        dev_testing: "Dev Testing",
        pending_uat: "Pending UAT",
        deployed_uat: "Deployed UAT",
        uat_failed: "UAT Failed",
        ready_for_prod: "Ready for Prod",
        prod_piv: "Prod / PIV",
      };
      return map[stage] || "Reported";
    },

    getWorkflowStageBadgeClass(stage) {
      const map = {
        reported: "bg-light text-secondary border",
        jira_logged: "bg-primary-subtle text-primary border border-primary-subtle",
        ai_triaging: "bg-info-subtle text-info border border-info-subtle",
        local_dev: "bg-cyan-subtle text-cyan-primary border",
        dev_testing: "bg-warning-subtle text-warning-emphasis border border-warning-subtle",
        pending_uat: "bg-warning-subtle text-warning-emphasis border border-warning-subtle",
        deployed_uat: "bg-primary-subtle text-primary border border-primary-subtle",
        uat_failed: "bg-danger-subtle text-danger border border-danger-subtle",
        ready_for_prod: "bg-success-subtle text-success border border-success-subtle",
        prod_piv: "bg-success text-white",
      };
      return map[stage] || "bg-light text-secondary border";
    },

    getWorkflowStageIcon(stage) {
      const found = this.workflowStages.find((s) => s.value === stage);
      return found ? found.icon : "fa-solid fa-flag";
    },

    getAIButtonLabel(report) {
      if (!report) return "Auto-Fix";
      if (report.ai_status === "fixed_local") return "AI Fixed";
      if (["queued", "triaging", "fixing"].includes(report.ai_status)) return "AI Queued";
      return "Auto-Fix";
    },

    triggerAIAutoFix(report) {
      if (!report || !report.id) return;
      this.queueingAiId = report.id;
      const payload = {
        id: report.id,
        user_id: this.getLoggedInUserId(),
        user_email: this.getLoggedInUserEmail() || this.userJiraCred.jira_email || "",
      };

      const handleSuccess = (res) => {
        if (res.data && res.data.success) {
          const updated = res.data.data || {};
          Object.assign(report, updated);
          if (this.activeTriageReport && this.activeTriageReport.id === report.id) {
            Object.assign(this.activeTriageReport, updated);
          }
          const idx = this.bugReports.findIndex((r) => r.id === report.id);
          if (idx !== -1) {
            Object.assign(this.bugReports[idx], updated);
          }
          const promptCmd = res.data.antigravity_prompt || `Triage & auto-fix Spark Bug Report #${report.id} (${report.jira_issue_key || "DEV"}): "${report.title}"`;
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(promptCmd).catch(() => {});
          }
          this.notify("success", `${res.data.message || "Queued for AI Auto-Fix!"} (AI Fix Command copied to clipboard)`);
        } else {
          this.notify("error", (res.data && res.data.message) || "Failed to queue bug report for AI Auto-Fix.");
        }
      };

      this.$axios
        .post(`/api/platform-bug-reporting/reports/${report.id}/ai-queue`, payload)
        .then(handleSuccess)
        .catch(() => {
          this.$axios
            .post(`/api/bug-reporting/reports/${report.id}/ai-queue`, payload)
            .then(handleSuccess)
            .catch((err) => {
              const errMsg = (err.response && err.response.data && err.response.data.message) || "Failed to queue report for AI Auto-Fix.";
              this.notify("error", errMsg);
            });
        })
        .finally(() => {
          this.queueingAiId = null;
        });
    },

    copyAIPromptForReport(report) {
      if (!report) return;
      this.triggerAIAutoFix(report);
    },

    submitAIFixUpdateToJira(report) {
      if (!report || !report.id) return;
      this.updatingAiId = report.id;
      const payload = {
        id: report.id,
        ai_status: report.ai_status || "fixed_local",
        ai_verdict: report.ai_verdict || "legit_bug",
        ai_analysis: report.ai_analysis || "",
        ai_branch: report.ai_branch || "dev",
        ai_commit_hash: report.ai_commit_hash || "",
        ai_files_changed: report.ai_files_changed || "",
        workflow_stage: report.workflow_stage || "local_dev",
        status: report.status || "in_progress",
        admin_notes: report.admin_notes || "",
        post_to_jira: true,
        user_id: this.getLoggedInUserId(),
        user_email: this.getLoggedInUserEmail() || this.userJiraCred.jira_email || "",
      };

      const handleSuccess = (res) => {
        if (res.data && res.data.success) {
          const updated = res.data.data || {};
          Object.assign(report, updated);
          if (this.activeTriageReport && this.activeTriageReport.id === report.id) {
            Object.assign(this.activeTriageReport, updated);
          }
          const idx = this.bugReports.findIndex((r) => r.id === report.id);
          if (idx !== -1) {
            Object.assign(this.bugReports[idx], updated);
          }
          this.notify("success", res.data.message || "AI Auto-Fix & SDLC stage synced to Jira!");
        } else {
          this.notify("error", (res.data && res.data.message) || "Failed to update AI Auto-Fix status.");
        }
      };

      this.$axios
        .post(`/api/platform-bug-reporting/reports/${report.id}/ai-update`, payload)
        .then(handleSuccess)
        .catch(() => {
          this.$axios
            .post(`/api/bug-reporting/reports/${report.id}/ai-update`, payload)
            .then(handleSuccess)
            .catch((err) => {
              const errMsg = (err.response && err.response.data && err.response.data.message) || "Failed to sync AI update to Jira.";
              this.notify("error", errMsg);
            });
        })
        .finally(() => {
          this.updatingAiId = null;
        });
    },

    saveTriageNotes() {
      if (!this.activeTriageReport) return;
      this.isSubmitting = true;
      this.$axios
        .put(`/api/platform-bug-reporting/reports/${this.activeTriageReport.id}`, this.activeTriageReport)
        .then((res) => {
          if (res.data && res.data.success) {
            this.notify("success", "Admin triage notes saved!");
            this.closeModalSafe("triageModal");
            this.fetchBugReports();
          }
        })
        .catch(() => {
          this.$axios
            .put(`/api/bug-reporting/reports/${this.activeTriageReport.id}`, this.activeTriageReport)
            .then((res) => {
              if (res.data && res.data.success) {
                this.notify("success", "Admin triage notes saved!");
                this.closeModalSafe("triageModal");
                this.fetchBugReports();
              }
            })
            .catch((err) => {
              console.error("Error saving triage notes:", err);
              this.notify("error", "Failed to save triage notes.");
            });
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },

    confirmDeleteReport(report) {
      if (confirm(`Are you sure you want to delete report "${report.title}"?`)) {
        this.isSubmitting = true;
        this.$axios
          .delete(`/api/platform-bug-reporting/reports/${report.id}`)
          .then((res) => {
            if (res.data && res.data.success) {
              this.notify("success", "Bug report deleted.");
              this.fetchBugReports();
            }
          })
          .catch(() => {
            this.$axios
              .delete(`/api/bug-reporting/reports/${report.id}`)
              .then((res) => {
                if (res.data && res.data.success) {
                  this.notify("success", "Bug report deleted.");
                  this.fetchBugReports();
                }
              })
              .catch((err) => {
                console.error("Error deleting report:", err);
                this.notify("error", "Failed to delete bug report.");
              });
          })
          .finally(() => {
            this.isSubmitting = false;
          });
      }
    },

    openScreenshotLightbox(report) {
      this.activeLightboxUrl = report.screenshot_url || report.screenshot_data;
      this.openModalSafe("screenshotLightboxModal");
    },

    openVideoModal(report) {
      this.activeVideoUrl = report.screen_recording_url || report.screen_recording_data;
      this.openModalSafe("videoPlayerModal");
    },

    openSandboxModal() {
      this.sandboxForm = {
        title: "Test Bug Submission from Interactive Sandbox",
        description: "Encountered styling layout shift when resizing floating widget drawer on mobile devices.",
        issue_type: "bug",
        severity: "medium",
        reporter_name: "Interactive Tester",
        reporter_email: "sandbox@vertex.com",
      };
      this.openModalSafe("sandboxSubmitModal");
      this.$nextTick(() => {
        this.initCanvas();
      });
    },

    openSandboxDrawer() {
      this.openSandboxModal();
    },

    initCanvas() {
      this.cancelSandboxCanvasText();
      const canvas = this.$refs.annotationCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#f8fafc";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = "#0f172a";
      ctx.font = "bold 14px sans-serif";
      ctx.fillText("[SIMULATED VIEWPORT CAPTURE]", 20, 40);

      ctx.strokeStyle = "#00a0c1";
      ctx.lineWidth = 2;
      ctx.strokeRect(20, 60, 300, 100);

      ctx.fillStyle = "#e11d48";
      ctx.fillRect(340, 60, 150, 40);
      ctx.fillStyle = "#ffffff";
      ctx.font = "12px sans-serif";
      ctx.fillText("HIGHLIGHT BUG", 350, 84);

      this.sandboxHistory = [ctx.getImageData(0, 0, canvas.width, canvas.height)];
    },

    selectSandboxAnnoTool(tool) {
      if (this.sandboxTextOverlay.visible) {
        if (this.sandboxTextOverlay.text && this.sandboxTextOverlay.text.trim()) {
          this.commitSandboxCanvasText();
        } else {
          this.cancelSandboxCanvasText();
        }
      }
      this.annoTool = tool;
    },

    getSandboxCanvasCoords(e) {
      const canvas = this.$refs.annotationCanvas;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / (rect.width || 1);
      const scaleY = canvas.height / (rect.height || 1);
      const clientX = e.clientX !== undefined ? e.clientX : (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
      const clientY = e.clientY !== undefined ? e.clientY : (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
      };
    },

    openSandboxTextOverlay(canvasX, canvasY, isShapeLabel = false) {
      const canvas = this.$refs.annotationCanvas;
      if (!canvas) return;
      const container = this.$refs.sandboxCanvasContainer || canvas.parentElement;
      const canvasRect = canvas.getBoundingClientRect();
      const containerRect = container ? container.getBoundingClientRect() : canvasRect;

      const scaleX = canvasRect.width / (canvas.width || 1);
      const scaleY = canvasRect.height / (canvas.height || 1);
      const offsetLeft = canvasRect.left - containerRect.left;
      const offsetTop = canvasRect.top - containerRect.top;

      let cssLeft = offsetLeft + canvasX * scaleX;
      let cssTop = offsetTop + canvasY * scaleY;

      cssLeft = Math.min(Math.max(8, cssLeft), Math.max(8, (containerRect.width || 400) - 290));
      cssTop = Math.min(Math.max(8, cssTop), Math.max(8, (containerRect.height || 180) - 44));

      this.sandboxTextOverlay = {
        visible: true,
        text: "",
        canvasX,
        canvasY,
        cssLeft,
        cssTop,
        isShapeLabel,
      };

      this.$nextTick(() => {
        if (this.$refs.sandboxCanvasTextInput) {
          this.$refs.sandboxCanvasTextInput.focus();
        }
      });
    },

    commitSandboxCanvasText() {
      if (!this.sandboxTextOverlay.visible) return;
      const text = (this.sandboxTextOverlay.text || "").trim();
      if (!text) {
        this.cancelSandboxCanvasText();
        return;
      }

      const canvas = this.$refs.annotationCanvas;
      if (!canvas) {
        this.cancelSandboxCanvasText();
        return;
      }

      const ctx = canvas.getContext("2d");
      const fontSize = 13;
      const padX = 8;
      const padY = 5;

      ctx.save();
      ctx.font = `bold ${fontSize}px Inter, system-ui, -apple-system, sans-serif`;
      ctx.textBaseline = "middle";
      const metrics = ctx.measureText(text);
      const boxW = metrics.width + padX * 2;
      const boxH = fontSize + padY * 2;

      let drawX = this.sandboxTextOverlay.canvasX;
      let drawY = this.sandboxTextOverlay.canvasY - boxH / 2;

      drawX = Math.min(Math.max(4, drawX), Math.max(4, canvas.width - boxW - 4));
      drawY = Math.min(Math.max(4, drawY), Math.max(4, canvas.height - boxH - 4));

      ctx.fillStyle = "rgba(15, 23, 42, 0.92)";
      ctx.fillRect(drawX, drawY, boxW, boxH);
      ctx.strokeStyle = this.annoColor;
      ctx.lineWidth = 2;
      ctx.strokeRect(drawX, drawY, boxW, boxH);

      ctx.fillStyle = "#ffffff";
      ctx.fillText(text, drawX + padX, drawY + boxH / 2);
      ctx.restore();

      this.sandboxHistory.push(ctx.getImageData(0, 0, canvas.width, canvas.height));
      this.cancelSandboxCanvasText();
    },

    cancelSandboxCanvasText() {
      this.sandboxTextOverlay = {
        visible: false,
        text: "",
        canvasX: 0,
        canvasY: 0,
        cssLeft: 0,
        cssTop: 0,
        isShapeLabel: false,
      };
    },

    startSandboxDrawing(e) {
      if (this.sandboxTextOverlay.visible) {
        if (this.sandboxTextOverlay.text && this.sandboxTextOverlay.text.trim()) {
          this.commitSandboxCanvasText();
        } else {
          this.cancelSandboxCanvasText();
        }
      }

      const coords = this.getSandboxCanvasCoords(e);
      if (this.annoTool === "text") {
        this.isSandboxDrawing = false;
        this.openSandboxTextOverlay(coords.x, coords.y, false);
        return;
      }

      this.isSandboxDrawing = true;
      this.sandboxStartX = coords.x;
      this.sandboxStartY = coords.y;
      this.sandboxLastX = coords.x;
      this.sandboxLastY = coords.y;

      const canvas = this.$refs.annotationCanvas;
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = this.annoColor;
      ctx.fillStyle = this.annoColor;
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      if (this.annoTool === "pen") {
        ctx.beginPath();
        ctx.moveTo(this.sandboxStartX, this.sandboxStartY);
      }
    },

    drawSandbox(e) {
      if (!this.isSandboxDrawing) return;
      const coords = this.getSandboxCanvasCoords(e);
      this.sandboxLastX = coords.x;
      this.sandboxLastY = coords.y;

      const canvas = this.$refs.annotationCanvas;
      const ctx = canvas.getContext("2d");

      if (this.annoTool === "pen") {
        ctx.lineTo(coords.x, coords.y);
        ctx.stroke();
      } else {
        if (this.sandboxHistory.length > 0) {
          ctx.putImageData(this.sandboxHistory[this.sandboxHistory.length - 1], 0, 0);
        }
        const w = coords.x - this.sandboxStartX;
        const h = coords.y - this.sandboxStartY;

        if (this.annoTool === "rect") {
          ctx.strokeStyle = this.annoColor;
          ctx.lineWidth = 3;
          ctx.strokeRect(this.sandboxStartX, this.sandboxStartY, w, h);
        } else if (this.annoTool === "arrow") {
          const headlen = 12;
          const angle = Math.atan2(coords.y - this.sandboxStartY, coords.x - this.sandboxStartX);
          ctx.beginPath();
          ctx.moveTo(this.sandboxStartX, this.sandboxStartY);
          ctx.lineTo(coords.x, coords.y);
          ctx.strokeStyle = this.annoColor;
          ctx.lineWidth = 3;
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(coords.x, coords.y);
          ctx.lineTo(
            coords.x - headlen * Math.cos(angle - Math.PI / 6),
            coords.y - headlen * Math.sin(angle - Math.PI / 6)
          );
          ctx.lineTo(
            coords.x - headlen * Math.cos(angle + Math.PI / 6),
            coords.y - headlen * Math.sin(angle + Math.PI / 6)
          );
          ctx.closePath();
          ctx.fillStyle = this.annoColor;
          ctx.fill();
        } else if (this.annoTool === "blur") {
          ctx.fillStyle = "rgba(15, 23, 42, 0.9)";
          ctx.fillRect(this.sandboxStartX, this.sandboxStartY, w, h);
          ctx.fillStyle = "#ffffff";
          ctx.font = "bold 11px sans-serif";
          ctx.fillText("REDACTED", this.sandboxStartX + 6, this.sandboxStartY + 16);
        }
      }
    },

    stopSandboxDrawing() {
      if (!this.isSandboxDrawing) return;
      this.isSandboxDrawing = false;
      const canvas = this.$refs.annotationCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      this.sandboxHistory.push(ctx.getImageData(0, 0, canvas.width, canvas.height));

      const dragDist = Math.hypot(this.sandboxLastX - this.sandboxStartX, this.sandboxLastY - this.sandboxStartY);
      if (dragDist > 12) {
        if (this.annoTool === "rect") {
          const anchorX = Math.min(this.sandboxStartX, this.sandboxLastX);
          const anchorY = Math.max(18, Math.min(this.sandboxStartY, this.sandboxLastY) - 12);
          this.openSandboxTextOverlay(anchorX, anchorY, true);
        } else if (this.annoTool === "arrow") {
          this.openSandboxTextOverlay(this.sandboxStartX, this.sandboxStartY, true);
        }
      }
    },

    startSandboxTouch(e) {
      if (e.touches && e.touches.length === 1) {
        this.startSandboxDrawing(e.touches[0]);
      }
    },

    drawSandboxTouch(e) {
      if (e.touches && e.touches.length === 1) {
        this.drawSandbox(e.touches[0]);
      }
    },

    undoSandboxCanvas() {
      this.cancelSandboxCanvasText();
      if (this.sandboxHistory.length > 1) {
        this.sandboxHistory.pop();
        const canvas = this.$refs.annotationCanvas;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        ctx.putImageData(this.sandboxHistory[this.sandboxHistory.length - 1], 0, 0);
      }
    },

    clearCanvas() {
      this.initCanvas();
    },

    async captureSandboxViewport() {
      this.cancelSandboxCanvasText();
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
          const stream = await navigator.mediaDevices.getDisplayMedia({
            video: true,
            audio: false,
            preferCurrentTab: true,
          });
          const video = document.createElement("video");
          video.srcObject = stream;
          await video.play();
          const canvas = this.$refs.annotationCanvas;
          if (canvas) {
            const ctx = canvas.getContext("2d");
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            this.sandboxHistory = [ctx.getImageData(0, 0, canvas.width, canvas.height)];
          }
          stream.getTracks().forEach((t) => t.stop());
          this.notify("success", "Live screen captured onto canvas!");
        }
      } catch (err) {
        // User cancelled screen picker — do not overwrite existing canvas or show error toast
      }
    },

    submitSandboxBugReport() {
      if (!this.currentPlatform) return;
      if (this.sandboxTextOverlay.visible) {
        if (this.sandboxTextOverlay.text && this.sandboxTextOverlay.text.trim()) {
          this.commitSandboxCanvasText();
        } else {
          this.cancelSandboxCanvasText();
        }
      }
      this.isSubmitting = true;

      const canvas = this.$refs.annotationCanvas;
      const screenshotData = canvas ? canvas.toDataURL("image/png") : "";

      const payload = {
        platform_id: this.currentPlatform.id,
        platform_key: this.currentPlatform.key,
        widget_uuid: this.widgetForm.widget_uuid || "",
        title: this.sandboxForm.title,
        description: this.sandboxForm.description,
        issue_type: this.sandboxForm.issue_type,
        severity: this.sandboxForm.severity,
        reporter_name: this.sandboxForm.reporter_name,
        reporter_email: this.sandboxForm.reporter_email,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        screenshot_data: screenshotData,
        screen_recording_data: "",
      };

      this.$axios
        .post(`/api/public/bug-widget/${encodeURIComponent(this.currentPlatform.key)}/submit`, payload)
        .then((res) => {
          if (res.data && res.data.success) {
            this.notify("success", "Bug report submitted live into workspace!");
            this.closeModalSafe("sandboxSubmitModal");
            this.activeTab = "reports";
            this.fetchBugReports();
            this.fetchPlatforms();
          }
        })
        .catch(() => {
          this.$axios
            .post(`/api/platform-bug-reporting/reports`, payload)
            .then((res) => {
              if (res.data && res.data.success) {
                this.notify("success", "Bug report submitted live into workspace!");
                this.closeModalSafe("sandboxSubmitModal");
                this.activeTab = "reports";
                this.fetchBugReports();
                this.fetchPlatforms();
              }
            })
            .catch(() => {
              this.$axios
                .post(`/api/bug-reporting/platforms/${this.currentPlatform.id}/reports`, payload)
                .then((res) => {
                  if (res.data && res.data.success) {
                    this.notify("success", "Bug report submitted live into workspace!");
                    this.closeModalSafe("sandboxSubmitModal");
                    this.activeTab = "reports";
                    this.fetchBugReports();
                    this.fetchPlatforms();
                  }
                })
                .catch((err3) => {
                  console.error("Error submitting bug report:", err3);
                  this.notify("error", "Failed to submit bug report.");
                });
            });
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },

    // Helpers
    notify(type, msg) {
      if (this.$toast && typeof this.$toast[type] === "function") {
        this.$toast[type](msg);
      } else {
        console.log(`[${type.toUpperCase()}] ${msg}`);
      }
    },

    openModalSafe(modalId) {
      const modalEl = document.getElementById(modalId);
      if (modalEl) {
        try {
          const bootstrapObj = (window.bootstrap && window.bootstrap.Modal) ? window.bootstrap : { Modal: BootstrapModal };
          if (bootstrapObj && bootstrapObj.Modal) {
            const modalInstance = bootstrapObj.Modal.getInstance(modalEl) || new bootstrapObj.Modal(modalEl);
            modalInstance.show();
          }
        } catch (e) {
          console.error("Error opening modal:", e);
        }
      }
    },

    closeModalSafe(modalId) {
      const modalEl = document.getElementById(modalId);
      if (modalEl) {
        const closeBtn = modalEl.querySelector('[data-bs-dismiss="modal"]');
        if (closeBtn) {
          closeBtn.click();
        } else {
          try {
            const bootstrapObj = (window.bootstrap && window.bootstrap.Modal) ? window.bootstrap : { Modal: BootstrapModal };
            if (bootstrapObj && bootstrapObj.Modal) {
              const modalInstance = bootstrapObj.Modal.getInstance(modalEl) || new bootstrapObj.Modal(modalEl);
              modalInstance.hide();
            }
          } catch (e) {
            console.error("Error closing modal:", e);
          }
        }
      }
      setTimeout(() => {
        const backdrops = document.querySelectorAll(".modal-backdrop");
        backdrops.forEach((el) => el.remove());
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      }, 150);
    },

    isUrl(str) {
      if (!str || typeof str !== "string") return false;
      return str.startsWith("http://") || str.startsWith("https://") || str.startsWith("/public/");
    },

    safePageUrl(url) {
      if (!url || typeof url !== "string") return "#";
      const trimmed = url.trim().toLowerCase();
      if (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("/")) {
        return url;
      }
      return "#";
    },

    hexToRgba(hex, alpha) {
      if (!hex || typeof hex !== "string" || !hex.startsWith("#")) {
        return `rgba(0, 160, 193, ${alpha || 0.1})`;
      }
      let c = hex.substring(1);
      if (c.length === 3) c = c.split("").map((x) => x + x).join("");
      const num = parseInt(c, 16);
      const r = (num >> 16) & 255;
      const g = (num >> 8) & 255;
      const b = num & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha || 0.1})`;
    },

    truncateUuid(uuid) {
      if (!uuid) return "N/A";
      if (uuid.length > 18) return uuid.substring(0, 14) + "...";
      return uuid;
    },

    copyText(text) {
      if (!text) return;
      navigator.clipboard.writeText(text);
      this.notify("success", "Copied to clipboard!");
    },

    copyCode(code) {
      this.copyText(code);
    },

    getPositionLabel(pos) {
      switch (pos) {
        case "right-edge": return "Right Edge Tab";
        case "bottom-right": return "Bottom-Right Pill";
        case "left-edge": return "Left Edge Tab";
        case "bottom-left": return "Bottom-Left Pill";
        case "top-right": return "Top-Right Trigger";
        default: return "Right Edge Tab";
      }
    },

    getWidgetPositionClass(pos) {
      switch (pos) {
        case "right-edge": return "pos-right-edge";
        case "bottom-right": return "pos-bottom-right";
        case "left-edge": return "pos-left-edge";
        case "bottom-left": return "pos-bottom-left";
        case "top-right": return "pos-top-right";
        default: return "pos-right-edge";
      }
    },

    getSandboxPopoverClass(pos) {
      switch (pos) {
        case "left-edge":
        case "bottom-left":
          return "sandbox-popover-left";
        case "top-right":
          return "sandbox-popover-top-right";
        case "right-edge":
        case "bottom-right":
        default:
          return "sandbox-popover-right";
      }
    },

    getSeverityBadgeClass(sev) {
      switch (sev) {
        case "critical": return "bg-danger text-white";
        case "high": return "bg-warning text-dark";
        case "medium": return "bg-info text-white";
        case "low": return "bg-secondary text-white";
        default: return "bg-info text-white";
      }
    },

    getStatusSelectClass(status) {
      switch (status) {
        case "open": return "bg-danger-subtle text-danger border-danger-subtle";
        case "in_progress": return "bg-warning-subtle text-dark border-warning-subtle";
        case "resolved": return "bg-success-subtle text-success border-success-subtle";
        case "closed": return "bg-secondary-subtle text-secondary border-secondary-subtle";
        default: return "bg-light text-dark";
      }
    },

    formatIssueType(type) {
      switch (type) {
        case "bug": return "Bug Report";
        case "feedback": return "Feedback";
        case "ui": return "UI / UX Issue";
        default: return "Bug Report";
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        return new Date(dateStr).toLocaleDateString("en-AU", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (e) {
        return dateStr;
      }
    },

    onIconFileChange(e, mode) {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (evt) => {
        if (mode === 'create') {
          this.platformForm.encoded_icon = evt.target.result.split(',')[1] || evt.target.result;
          this.platformForm.icon_filename = file.name;
        }
      };
      reader.readAsDataURL(file);
    },
  },
};
</script>

<style scoped>
.platform-bug-reporting-page {
  background-color: transparent;
}

.text-dark-blue {
  color: #1e293b;
}

.text-muted-custom {
  color: #64748b;
}

.text-xxs {
  font-size: 11px;
}

.text-xs {
  font-size: 12px;
}

.text-sm {
  font-size: 13.5px;
}

.bg-purple-subtle {
  background-color: #f3e8ff !important;
}

.text-purple {
  color: #7e22ce !important;
}

.border-purple-subtle {
  border-color: #d8b4fe !important;
}

/* ── Page Header Styles (Exact match with PlatformReleases & Regression Library) ── */
.ms-page-header {
  background: #ffffff;
  padding: 1.25rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
}

.ms-page-header__icon {
  width: 44px;
  height: 44px;
  background-color: #f0f9ff;
  color: #00a0c1;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
}

.ms-page-header__breadcrumb {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 8px;
  font-weight: 600;
}

.ms-breadcrumb-sep {
  margin: 0 4px;
  color: #cbd5e1;
}

.ms-page-header__title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 0;
  line-height: 1.2;
}

/* ── Custom Styled Action Buttons ── */
.btn-header-secondary {
  background-color: #ffffff !important;
  color: #334155 !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1) !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;
}

.btn-header-secondary:hover,
.btn-header-secondary:focus,
.btn-header-secondary:active {
  background-color: #f1f5f9 !important;
  color: #0f172a !important;
  border-color: #94a3b8 !important;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.06) !important;
}

.btn-header-secondary:hover i,
.btn-header-secondary:focus i,
.btn-header-secondary:active i {
  color: #008ca9 !important;
}

.btn-outline-secondary {
  background-color: #ffffff !important;
  color: #475569 !important;
  border: 1px solid #cbd5e1 !important;
  transition: all 0.15s ease-in-out !important;
}

.btn-outline-secondary:hover,
.btn-outline-secondary:focus,
.btn-outline-secondary:active {
  background-color: #f1f5f9 !important;
  color: #0f172a !important;
  border-color: #94a3b8 !important;
}

.btn-cyan-primary {
  background-color: #00a0c1 !important;
  color: #ffffff !important;
  border: 1px solid #00a0c1 !important;
  border-radius: 8px !important;
  transition: all 0.2s ease-in-out !important;
  box-shadow: 0 2px 4px rgba(0, 160, 193, 0.2) !important;
}

.btn-cyan-primary:hover {
  background-color: #008ca9 !important;
  border-color: #008ca9 !important;
  color: #ffffff !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 160, 193, 0.3) !important;
}

.btn-cyan-outline {
  background-color: #ffffff !important;
  color: #00a0c1 !important;
  border: 1.5px solid #00a0c1 !important;
  border-radius: 8px !important;
  transition: all 0.2s ease-in-out !important;
}

.btn-cyan-outline:hover {
  background-color: #e6f7fa !important;
  color: #008ca9 !important;
  border-color: #008ca9 !important;
  transform: translateY(-1px);
}

.bg-cyan-primary {
  background-color: #00a0c1 !important;
  color: #ffffff !important;
}

.bg-cyan-subtle {
  background-color: rgba(0, 160, 193, 0.12) !important;
}

.text-cyan-primary {
  color: #00a0c1 !important;
}

.border-cyan-subtle {
  border-color: rgba(0, 160, 193, 0.3) !important;
}

.btn-xs {
  font-size: 11.5px;
  padding: 4px 10px;
}

.version-pill-highlight-sm {
  background: linear-gradient(135deg, #00a0c1 0%, #00738c 100%) !important;
  color: #ffffff !important;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace !important;
  font-size: 11.5px !important;
  font-weight: 800 !important;
  padding: 4px 12px !important;
  border-radius: 12px !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 1px 4px rgba(0, 160, 193, 0.3) !important;
  border: 1px solid rgba(255, 255, 255, 0.25) !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 6px !important;
  line-height: 1.4 !important;
  vertical-align: middle !important;
}

.delete-btn {
  transition: all 0.2s ease;
}

.delete-btn:hover {
  background-color: #dc3545 !important;
  color: #ffffff !important;
  border-color: #dc3545 !important;
}

.delete-icon-box {
  width: 56px;
  height: 56px;
  background-color: #fef2f2;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Platform Switcher & Banner Cards */
.platform-color-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
}

.platform-avatar-img,
.platform-banner-logo-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 6px;
}

.platform-select-btn {
  transition: all 0.2s ease;
}

.platform-select-btn:hover {
  background-color: #f8fafc !important;
  border-color: #cbd5e1 !important;
}

.platform-banner-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0 !important;
}

.platform-icon-avatar,
.platform-banner-avatar {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  flex-shrink: 0;
}

/* ── Project Card (Exact match with PlatformReleases) ── */
.project-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  overflow: hidden;
  cursor: pointer;
}

.project-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08) !important;
  border-color: #00a0c1 !important;
}

.project-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  transition: transform 0.2s ease;
}

.project-card-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 6px;
}

.card-project-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  transition: color 0.15s ease;
}

.project-card:hover .card-project-title {
  color: #00a0c1;
}

.card-settings-btn {
  color: #94a3b8;
  transition: color 0.15s ease, transform 0.15s ease;
}

.card-settings-btn:hover {
  color: #00a0c1 !important;
  transform: rotate(30deg);
}

.card-delete-btn {
  color: #cbd5e1;
  transition: color 0.15s ease, transform 0.15s ease;
}

.card-delete-btn:hover {
  color: #ef4444 !important;
  transform: scale(1.15);
}

.text-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-action-link {
  transition: color 0.15s ease;
}

.card-arrow {
  transition: transform 0.2s ease;
}

.project-card:hover .card-arrow {
  transform: translateX(5px);
}

/* ── Top-Level Tabs Custom Styling (Cyan underline tabs) ── */
.nav-tabs {
  border-bottom: 2px solid #e9ecef !important;
}

.nav-tabs .nav-link {
  border: none !important;
  color: #6c757d !important;
  background: transparent !important;
  transition: all 0.2s ease;
  position: relative;
  padding: 12px 24px !important;
}

.nav-tabs .nav-link:hover {
  color: #00a0c1 !important;
}

.nav-tabs .nav-link.active {
  color: #00a0c1 !important;
  background: transparent !important;
}

.nav-tabs .nav-link.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background-color: #00a0c1;
  border-radius: 3px;
}

/* Filter Toolbar Card & Segmented Controls */
.filter-toolbar-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0 !important;
}

.category-pills-group {
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
}

.category-pills-group .btn,
.category-filter-pill {
  font-size: 12px;
  border: none;
  transition: all 0.15s ease-in-out;
}

.category-pills-group .btn.active-filter,
.category-filter-pill.active-filter {
  background-color: #00a0c1 !important;
  color: #ffffff !important;
  box-shadow: 0 1px 3px rgba(0, 160, 193, 0.25);
}

.category-pills-group .btn:not(.active-filter):hover,
.category-filter-pill:not(.active-filter):hover {
  background-color: #e2e8f0 !important;
  color: #1e293b !important;
}

.changelog-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0 !important;
  transition: all 0.2s ease;
}

.changelog-card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05) !important;
}

/* Search Glow */
.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
}

.focus-glow:focus {
  background: #ffffff !important;
  box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
  border-color: #00a0c1 !important;
}

.shadow-xs {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.color-preset-btn {
  width: 24px;
  height: 24px;
  cursor: pointer;
  transition: transform 0.15s ease;
}

.color-preset-btn:hover,
.color-preset-btn.active {
  transform: scale(1.2);
  box-shadow: 0 0 0 2px #1e293b;
}

/* ── Widget Orientation Visual Cards & Mini Screen Diagram ── */
.position-radio-card {
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background-color: #f8fafc;
  cursor: pointer;
  transition: all 0.2s ease;
}

.position-radio-card:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
}

.position-radio-card.active-pos-card {
  background-color: #f0f9ff;
  border-color: #00a0c1 !important;
  box-shadow: 0 0 0 2px rgba(0, 160, 193, 0.18);
}

.mini-screen-diagram {
  width: 44px;
  height: 32px;
  background-color: #ffffff;
  border: 1.5px solid #cbd5e1;
  border-radius: 5px;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.04);
}

.position-radio-card.active-pos-card .mini-screen-diagram {
  border-color: #94a3b8;
}

.mini-screen-topbar {
  height: 6px;
  background-color: #e2e8f0;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0 3px;
}

.mini-screen-topbar span {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background-color: #94a3b8;
}

.mini-screen-indicator {
  position: absolute;
  border-radius: 2px;
}

.indicator-right-edge {
  right: 0;
  top: 55%;
  transform: translateY(-50%);
  width: 5px;
  height: 14px;
  border-radius: 3px 0 0 3px;
}

.indicator-left-edge {
  left: 0;
  top: 55%;
  transform: translateY(-50%);
  width: 5px;
  height: 14px;
  border-radius: 0 3px 3px 0;
}

.indicator-bottom-right {
  right: 3px;
  bottom: 3px;
  width: 12px;
  height: 5px;
  border-radius: 99px;
}

.indicator-bottom-left {
  left: 3px;
  bottom: 3px;
  width: 12px;
  height: 5px;
  border-radius: 99px;
}

.indicator-top-right {
  right: 3px;
  top: 8px;
  width: 12px;
  height: 5px;
  border-radius: 99px;
}

/* ── Sandbox Unified Drawer Assembly & Docking Positions ── */
.sandbox-viewport-body {
  overflow: hidden;
}

.sandbox-drawer-unit {
  position: absolute;
  z-index: 30;
  display: flex;
  align-items: center;
  pointer-events: none;
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}

.sandbox-widget-trigger {
  pointer-events: auto;
  color: #ffffff;
  font-weight: 700;
  font-size: 12px;
  padding: 9px 16px;
  border-radius: 999px;
  cursor: pointer;
  z-index: 31;
  transition: filter 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  user-select: none;
  flex-shrink: 0;
}

.sandbox-widget-trigger:hover {
  filter: brightness(1.12);
  box-shadow: 0 4px 18px rgba(0, 160, 193, 0.45) !important;
}

/* Right Edge Unified Sliding Assembly */
.sandbox-drawer-unit.pos-right-edge {
  right: 0;
  top: 50%;
  flex-direction: row;
  transform: translate3d(100%, -50%, 0);
}

.sandbox-drawer-unit.pos-right-edge.is-open {
  transform: translate3d(0, -50%, 0);
}

.sandbox-drawer-unit.pos-right-edge .sandbox-widget-trigger {
  position: absolute;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-right: 0;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  border-radius: 8px 0 0 8px;
  border-right: none;
  padding: 12px 8px;
}

.sandbox-drawer-unit.pos-right-edge .sandbox-widget-trigger:hover {
  transform: translateY(-50%);
}

.sandbox-drawer-unit.pos-right-edge .sandbox-inline-popover {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: none !important;
  box-shadow: none !important;
}

.sandbox-drawer-unit.pos-right-edge.is-open .sandbox-inline-popover {
  box-shadow: -8px 8px 24px rgba(15, 23, 42, 0.18) !important;
}

/* Left Edge Unified Sliding Assembly */
.sandbox-drawer-unit.pos-left-edge {
  left: 0;
  top: 50%;
  flex-direction: row-reverse;
  transform: translate3d(-100%, -50%, 0);
}

.sandbox-drawer-unit.pos-left-edge.is-open {
  transform: translate3d(0, -50%, 0);
}

.sandbox-drawer-unit.pos-left-edge .sandbox-widget-trigger {
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 0;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  border-radius: 0 8px 8px 0;
  border-left: none;
  padding: 12px 8px;
}

.sandbox-drawer-unit.pos-left-edge .sandbox-widget-trigger:hover {
  transform: translateY(-50%);
}

.sandbox-drawer-unit.pos-left-edge .sandbox-inline-popover {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left: none !important;
  box-shadow: none !important;
}

.sandbox-drawer-unit.pos-left-edge.is-open .sandbox-inline-popover {
  box-shadow: 8px 8px 24px rgba(15, 23, 42, 0.18) !important;
}

/* Corner Floating Pill Orientations */
.sandbox-drawer-unit.pos-bottom-right {
  bottom: 18px;
  right: 18px;
  flex-direction: column-reverse;
  align-items: flex-end;
}

.sandbox-drawer-unit.pos-bottom-left {
  bottom: 18px;
  left: 18px;
  flex-direction: column-reverse;
  align-items: flex-start;
}

.sandbox-drawer-unit.pos-top-right {
  top: 18px;
  right: 18px;
  flex-direction: column;
  align-items: flex-end;
}

.sandbox-drawer-unit.pos-bottom-right .sandbox-inline-popover,
.sandbox-drawer-unit.pos-bottom-left .sandbox-inline-popover,
.sandbox-drawer-unit.pos-top-right .sandbox-inline-popover {
  position: absolute;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    visibility 0.28s ease;
}

.sandbox-drawer-unit.pos-bottom-right .sandbox-inline-popover {
  right: 0;
  bottom: calc(100% + 10px);
  transform: translate3d(0, 12px, 0) scale(0.96);
}

.sandbox-drawer-unit.pos-bottom-left .sandbox-inline-popover {
  left: 0;
  bottom: calc(100% + 10px);
  transform: translate3d(0, 12px, 0) scale(0.96);
}

.sandbox-drawer-unit.pos-top-right .sandbox-inline-popover {
  right: 0;
  top: calc(100% + 10px);
  transform: translate3d(0, -12px, 0) scale(0.96);
}

.sandbox-drawer-unit.pos-bottom-right.is-open .sandbox-inline-popover,
.sandbox-drawer-unit.pos-bottom-left.is-open .sandbox-inline-popover,
.sandbox-drawer-unit.pos-top-right.is-open .sandbox-inline-popover {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: translate3d(0, 0, 0) scale(1);
}

/* ── Inline Miniature Popover Preview Inside Sandbox ── */
.sandbox-inline-popover {
  pointer-events: auto;
  width: 255px;
  border-radius: 12px;
  z-index: 25;
  overflow: hidden;
  transition: box-shadow 0.35s ease, background-color 0.2s ease, border-color 0.2s ease;
}

.sandbox-popover-light {
  background-color: #ffffff;
  color: #0f172a;
  border-color: #cbd5e1 !important;
}

.sandbox-popover-dark {
  background-color: #0f172a;
  color: #f8fafc;
  border-color: #334155 !important;
}

.sandbox-popover-dark .border-bottom,
.sandbox-popover-dark .border {
  border-color: #334155 !important;
}

/* Custom Scrollable Modals - Guaranteed Visible Header & Footer */
.custom-modal-scrollable {
  max-height: calc(100vh - 2rem) !important;
  display: flex !important;
  flex-direction: column !important;
}

.custom-modal-scrollable .modal-content {
  max-height: calc(100vh - 2.5rem) !important;
  display: flex !important;
  flex-direction: column !important;
  border-radius: 12px;
  overflow: hidden;
}

.custom-modal-scrollable .modal-header {
  flex-shrink: 0;
  background-color: #ffffff;
  z-index: 1055;
  border-bottom: 1px solid #e2e8f0;
}

.custom-modal-scrollable .custom-modal-body {
  flex: 1 1 auto;
  overflow-y: auto !important;
  max-height: calc(100vh - 160px) !important;
  padding: 1.25rem 1.5rem !important;
}

.custom-modal-scrollable .modal-footer {
  flex-shrink: 0;
  background-color: #f8fafc;
  z-index: 1055;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.03);
}

.screenshot-thumb-wrapper .screenshot-thumb-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(15, 23, 42, 0.7);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.screenshot-thumb-wrapper:hover .screenshot-thumb-overlay {
  opacity: 1;
}

.sandbox-canvas-text-overlay {
  position: absolute;
  z-index: 20;
  background: rgba(15, 23, 42, 0.94);
  backdrop-filter: blur(4px);
  border: 2px solid #ef4444;
  border-radius: 8px;
  padding: 4px 8px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.35);
  min-width: 240px;
  max-width: 320px;
}

.sandbox-canvas-text-input {
  background: transparent;
  border: none;
  outline: none;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  flex: 1;
  min-width: 140px;
}

.sandbox-canvas-text-input::placeholder {
  color: rgba(255, 255, 255, 0.55);
  font-weight: 400;
}

/* Vertex Teal (#00A0C1) Toggle Switch Overrides */
.form-check-input {
  cursor: pointer;
}

.form-check-input:checked,
.form-switch .form-check-input:checked {
  background-color: #00A0C1 !important;
  border-color: #00A0C1 !important;
}

.form-check-input:focus,
.form-switch .form-check-input:focus {
  border-color: #00A0C1 !important;
  box-shadow: 0 0 0 0.2rem rgba(0, 160, 193, 0.25) !important;
}

/* Compact Color Swatches & Streamlined Repository List */
.color-preset-btn-sm {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid transparent;
  padding: 0;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.color-preset-btn-sm:hover {
  transform: scale(1.15);
}

.color-preset-btn-sm.active {
  border-color: #0f172a;
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 3px #00A0C1;
}

.repo-attached-row {
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.repo-attached-row:hover {
  border-color: #bae6fd !important;
  box-shadow: 0 1px 3px rgba(0, 160, 193, 0.08);
}

.repo-role-badge-select {
  appearance: none;
  -webkit-appearance: none;
  background-color: #ecfeff;
  color: #0891b2;
  border: 1px solid #a5f3fc;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding: 2px 6px;
  text-align: center;
  cursor: pointer;
  min-width: 52px;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.repo-role-badge-select:hover,
.repo-role-badge-select:focus {
  background-color: #cffafe;
  border-color: #00A0C1;
  outline: none;
}

.hover-danger {
  transition: color 0.15s ease, background-color 0.15s ease;
}

.hover-danger:hover {
  color: #dc2626 !important;
  background-color: #fef2f2 !important;
}
</style>
